import { Fragment, useEffect, useState, useCallback, useRef } from 'react'
import {
  Box, Typography, Paper, Stack, ToggleButton, ToggleButtonGroup, TextField, Button,
  CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText,
  Collapse, Divider, useTheme, Tabs, Tab, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, Tooltip,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { fetchReportingSummary, fetchContactsPerHour, fetchContactsPerHourByIntent, fetchHandleTime, fetchAsaByAccount, fetchAhtByAccount, fetchAhtByAccountPerRep, fetchScorecards, fetchLongestAhtContacts, fetchContactHandleTime, fetchLongestAhtSummary, fetchQaKpiBlendedAht, fetchQaKpiAsaByAccount, fetchAutomationTrends, fetchAutomationTrendsSummary, fetchOnlineTime, fetchLiveAgents, fetchLiveQueues, downloadWeeklyExport, downloadWeeklyLaborUtilization } from '../../api.js'
import LineChart from '../../LineChart.jsx'
import ContactsPerOrder from '../ContactsPerOrder.jsx'
import ArrivalHeatmap from '../../ArrivalHeatmap.jsx'
import { aggregateContactsPerHour, aggregateAhtByIntent } from '../../contactsPerHourAggregate.js'
import { aggregateHandleTime, pooledStats } from '../../handleTimeAggregate.js'
import { agentIntentWithBaseline, outlierRows, MIN_OUTLIER_COUNT, OUTLIER_THRESHOLD_PCT } from '../../ahtByIntentAnalysis.js'
import { poolAgentRows } from '../../ahtByRepPooling.js'
import { poolScorecardRows, poolScorecardTotal } from '../../scorecardsPooling.js'
import { pauseLabel, releaseLabel, eventLabel, formatEt, formatMinutes } from '../../contactHandleTimeline.js'
import { buildScorecardsCsv } from '../../scorecardsExport.js'
import { poolAutomationRate, poolAutomationFailures } from '../../automationTrendPooling.js'
import { buildAhtByAccountPerRepCsv, downloadCsvText } from '../../ahtByRepExport.js'
import { agentsInTeam, teamsPresent } from '../../constants/teams.js'
import { exportChartPng } from '../../exportChartPng.js'
import { INTENT_COLOR, intentLabel } from '../../constants/intents.js'
import ScorecardsFocusPanel from '../ScorecardsFocusPanel.jsx'
import BpEmailStatsPanel from '../BpEmailStatsPanel.jsx'
import ForecastingTab from '../ForecastingTab.jsx'
import SectionHeading from '../SectionHeading.jsx'
import { KNOWN_COMPANIES } from '../../constants/companies.js'
import { CHANNEL_LABELS } from '../../constants/channels.js'

const ASA_WEEK_OPTIONS = [4, 8, 13, 26]

// AHT by account per rep's own periods-back options, one list per
// granularity (day/week/month) -- the weekly list intentionally matches
// ASA_WEEK_OPTIONS's values so the two "N weeks back" controls on this page
// stay consistent, even though this one is keyed separately since it also
// has day/month lists ASA/AHT-by-account never needed.
const AHT_REP_PERIODS_OPTIONS = {
  day: [7, 14, 30],
  week: [4, 8, 13, 26],
  month: [3, 6, 12],
}
// '1A_EMAIL'/'AM_EMAIL' (added 2026-09-29) are synthetic pooled columns, not
// literal account codes -- see AHT_PER_REP_COLUMNS in reporting.js for
// exactly what each pools (AM_EMAIL = AM_WEBSITE + AM_INTERNAL_EMAIL;
// 1A_EMAIL is 1A_INTERNAL_EMAIL alone, no real 1A Website account exists yet).
// SUPPORT_EMAIL (added 2026-10-06) is also synthetic -- 1A's Sales Hold
// Callback carve-out, split out of 1A Email so this report's numbers agree
// with every other report that already applies the carve-out (ASA by
// account, My Stats, Scorecards). No AM equivalent (AM never runs this
// callback flow), so AM_EMAIL's own contacts/AHT are unaffected.
const AHT_REP_ACCOUNTS = ['1A_EBAY', 'AM_EBAY', '1A_EMAIL', 'AM_EMAIL', 'SUPPORT_EMAIL']
const AHT_REP_ACCOUNT_LABELS = { '1A_EBAY': '1A eBay', AM_EBAY: 'AM eBay', '1A_EMAIL': '1A Email', AM_EMAIL: 'AM Email', SUPPORT_EMAIL: 'Support Email' }
// Full grid borders on every cell, matching the external SLA tool this report
// replaces -- MUI's Table only draws a bottom rule per row by default.
const ASA_CELL_BORDER = { border: '1px solid', borderColor: 'divider' }

// Automation Reports' own account-group filter (2026-10-07, Patrick's
// request). Same pooled groups as AHT_REP_ACCOUNTS/AHT_REP_ACCOUNT_LABELS --
// '1A_EMAIL'/'AM_EMAIL' pool each brand's Website + Internal-notice accounts
// -- but labeled "Website" here, Patrick's own wording for this section (the
// identical data reads "Email" on AHT by account per rep; no inconsistency,
// just this section's own label choice). SUPPORT_EMAIL is included even
// though only 1A runs it today -- a forward-looking bucket for future email
// queues that migrate under it.
const AUTOMATION_TREND_GROUPS = ['1A_EBAY', 'AM_EBAY', '1A_EMAIL', 'AM_EMAIL', 'SUPPORT_EMAIL']
const AUTOMATION_TREND_GROUP_LABELS = { '1A_EBAY': '1A eBay', AM_EBAY: 'AM eBay', '1A_EMAIL': '1A Website', AM_EMAIL: 'AM Website', SUPPORT_EMAIL: 'Support Email' }

// Day/Week only -- not the 3-option GRANULARITIES (which includes Monthly,
// not offered for Handle Time).
const HT_GRANULARITIES = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
]

// Shown live in the UI next to per-agent duration data, and baked into the
// PNG export so the caveat survives once the image leaves the app -- claim
// span is not effort, and per-agent numbers are not a performance measure.
const HT_NOT_A_PERFORMANCE_MEASURE = 'Claimed time, not effort — not a performance measure.'

// Section nav for the sticky header's jump-link row -- split by tab, since a
// section only exists in the DOM on whichever tab it currently lives under.
// Moving a section between tabs means moving its entry between these two
// lists, not just its <Box>.
const ACCOUNT_NAV_SECTIONS = [
  ['#section-volume', 'Volume'],
  ['#section-response', 'Response time'],
  ['#section-handle', 'Handle time'],
  ['#section-longest-aht', 'Longest AHT contacts'],
  ['#section-aht', 'AHT by account'],
  ['#section-asa', 'ASA by account'],
  ['#section-aht-per-rep', 'AHT by account per rep'],
  ['#section-cph', 'Contacts/hr'],
  ['#section-cphi', 'AHT · intent'],
  ['#section-aht-by-intent', 'AHT by intent'],
  ['#section-aht-by-intent-rep', 'AHT by intent · per rep'],
  ['#section-aht-outliers', 'AHT outliers'],
  ['#section-cpo', 'Contacts per Order'],
]
const LEGACY_NAV_SECTIONS = [
  ['#section-arrival', 'Arrival pattern'],
  ['#section-intent', 'By intent'],
  ['#section-agent', 'By agent'],
  ['#section-online', 'Online vs. active'],
  ['#section-spam-rules', 'Spam rules'],
]
const AUTOMATION_NAV_SECTIONS = [
  ['#section-automation-rate', 'Automation rate'],
  ['#section-automation-failures', 'Automation failures'],
]
const DASHBOARD_NAV_SECTIONS = [
  ['#section-live-agents', 'Agent states'],
  ['#section-live-queue', 'Live queue'],
]
// Weekly Report is a short, single-screen tab (two download buttons) --
// no sections to jump to, but still needs its OWN entry here so it doesn't
// fall through to LEGACY_NAV_SECTIONS in the ternary below.
const WEEKLY_NAV_SECTIONS = []
// Preview of CXHub aggregate metrics (starting with ASA by account) planned
// for exposure to QA System's own KPI Admin via a future read-only, Entra
// client-credentials API (qa_reader role, reusing Gordon's existing /api/qa
// boundary -- see docs/decisions.md and project_bsa_reporting_api memory).
// This tab is a human-facing visibility/audit layer only (what the numbers
// look like today); QA System will pull the data itself server-to-server
// once that API exists, it does not read this tab.
const QA_KPI_NAV_SECTIONS = [
  ['#section-qa-kpi-asa', 'ASA by account'],
  ['#section-qa-kpi-aht', "AHT's"],
]
// Scorecards -- a supervisor's per-agent, email-channel view (2026-10-06,
// Patrick's request), independent of the page's shared company/date
// controls (same reasoning Dashboard's own independence has, see the
// `reportingTab !== 'dashboard'` check below) -- each sub-table owns its own
// period/team/agent filter instead.
const SCORECARDS_NAV_SECTIONS = [
  ['#section-scorecards-week', 'Week'],
  ['#section-scorecards-month', 'Month'],
]
// Forecasting (2026-10-16) -- Patrick's own working area (see
// canSeeForecastingTab's own comment for why this tab is gated to one person
// rather than admin-wide). No jump links yet.
const FORECASTING_NAV_SECTIONS = []
// Sourced from qaKpiAsaData, NOT the shared asaData the Account Reports ASA
// table below uses (2026-10-15 fix) -- asaData comes from
// getAsaByAccountWithSupportEmailReport, which pools each brand's Website
// into its Email row (EMAIL_POOLED_ACCOUNT_GROUPS, reporting.js) and no
// longer emits a standalone 1A_WEBSITE/AM_WEBSITE account at all, so these
// keys never matched anything there -- permanent dashes regardless of real
// traffic. Patrick needs Website's own SLA reported separately from each
// brand's Internal-notice mailbox, so this preview deliberately uses the
// plain, unpooled getAsaByAccountReport instead (see qaKpiAsaData's own
// fetch effect) -- true per-account numbers, matching what an external QA
// System consumer would see for these account codes.
const QA_KPI_ASA_ACCOUNTS = ['1A_EBAY', 'AM_EBAY', '1A_WEBSITE', 'AM_WEBSITE', 'WALMART']
const QA_KPI_ASA_ACCOUNT_LABELS = { '1A_EBAY': '1A eBay', AM_EBAY: 'AM eBay', '1A_WEBSITE': '1A Website', AM_WEBSITE: 'AM Website', WALMART: 'Walmart' }

// Polling cadence for the Dashboard tab (a "right now" snapshot, unlike
// every other tab's date-ranged report) -- admin-only viewership already
// keeps this cheap; 15s trades a little freshness for lower load.
const DASHBOARD_POLL_MS = 15_000

// Caps the AHT outliers table so it stays a scannable coaching list rather
// than a wall of rows once a real date range/roster is behind it.
const AHT_OUTLIERS_LIMIT = 25

// Longest AHT contacts starts showing this many, "Load more" reveals this
// many at a time -- same client-slice pattern AHT_OUTLIERS_LIMIT uses, just
// paged instead of a single hard cap since Patrick asked for "top 10 with an
// option to load more" specifically.
const LONGEST_AHT_PAGE_SIZE = 10

const DEFAULT_COMPANIES = ['1A']
// Channel scope for Account / Legacy reports (2026-10-19). They were eBay-only
// until email became most of the work; "All" is every customer channel.
const CHANNEL_OPTIONS = [
  { value: 'all', label: 'All channels' },
  { value: 'ebay', label: 'eBay' },
  { value: 'email', label: 'Email' },
]

const DEFAULT_INTENT_COUNT = 5
const DEFAULT_AGENT_COUNT = 5

// Resolves an intent's chip color name (from INTENT_COLOR) to a hex value
// for the chart lines, so intent colors stay consistent with intent chips
// shown elsewhere in the app. 'default' isn't a real MUI palette channel.
function intentHexColor(theme, intentKey) {
  const colorName = INTENT_COLOR[intentKey]
  return theme.palette[colorName]?.main ?? theme.palette.text.secondary
}

// Adaptive duration formatting for wait times: minutes below an hour, hours
// below two days, days beyond that.
function formatDuration(hours) {
  if (hours == null) return '—'
  if (hours < 1) return `${Math.round(hours * 60)}m`
  if (hours < 48) return `${hours.toFixed(1)}h`
  return `${(hours / 24).toFixed(1)}d`
}

const nf = new Intl.NumberFormat('en-US')
function fmtCount(n) {
  return nf.format(n ?? 0)
}

// A rule is flagged stale once it's gone this long without a match — the
// signal that it may be dead weight worth reviewing/retiring, as distinct
// from a rule that's simply too new to have fired yet.
const STALE_DAYS = 30

// "Last matched" for a spam rule: lastMatchedAt/createdAt are raw timestamps
// from getSpamRuleReport (lastMatchedAt looks back STALE_LOOKBACK_DAYS from
// reporting.js, independent of the page's selected date range). A rule with
// no match on record reads "No matches yet" while it's still young, but
// "Never matched" (and stale-styled) once it's been around longer than the
// threshold with nothing to show for it.
function formatLastMatched(lastMatchedAt, createdAt) {
  if (!lastMatchedAt) {
    const ruleAgeDays = (Date.now() - new Date(createdAt).getTime()) / 86400000
    return ruleAgeDays > STALE_DAYS
      ? { label: 'Never matched', stale: true }
      : { label: 'No matches yet', stale: false }
  }
  const days = Math.floor((Date.now() - new Date(lastMatchedAt).getTime()) / 86400000)
  const label = days < 1 ? 'Today' : days === 1 ? '1d ago' : `${days}d ago`
  return { label, stale: days > STALE_DAYS }
}

// Handle-time durations, from ms. Sub-hour values read as m:ss (the range
// most contacts land in, where seconds still carry information); anything
// longer switches to h:mm:ss. Matches ConversationActivity.jsx's per-contact
// display so the same contact reads the same on both screens.
function formatHandleMs(ms) {
  if (typeof ms !== 'number' || !isFinite(ms)) return '—'
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (n) => String(n).padStart(2, '0')
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
}

// AHT by account per rep: flag anything over 4 minutes so it stands out
// (Patrick's ask, 2026-10-13). Light red, theme-aware so it reads correctly
// in dark mode too -- same alpha(error.light) treatment SuggestionPanel.jsx
// already uses for its own warning callouts.
const AHT_REP_HIGHLIGHT_THRESHOLD_MS = 4 * 60 * 1000
function ahtHighlightSx(ms) {
  return typeof ms === 'number' && isFinite(ms) && ms > AHT_REP_HIGHLIGHT_THRESHOLD_MS
    ? { bgcolor: (theme) => alpha(theme.palette.error.light, 0.18), color: 'error.main', fontWeight: 700 }
    : {}
}

// Hours (float, or null for "no data") -> H:MM:SS, matching the external SLA
// tool's format this report replaces (see the screenshot in the plan) — kept
// distinct from formatHandleMs (which formats ms, for a different report)
// since the two are never fed the same unit.
function formatAsaHours(hours) {
  if (hours == null) return '—'
  const totalSeconds = Math.max(0, Math.round(hours * 3600))
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

// ADH% (schedule adherence, from Schedule Hub) as a fraction (0-1) ->
// "98.43%" -- two decimal places, same precision MyStatsPage.jsx's own
// formatPercent uses (close-together real weekly numbers made whole-percent
// rounding hide real movement). null (Schedule Hub disabled, the request
// failed, or no matching agent_permissions row) reads as "—", never 0%.
function formatPercent(fraction) {
  if (typeof fraction !== 'number' || !isFinite(fraction)) return '—'
  return `${(fraction * 100).toFixed(2)}%`
}

// Scorecards' "Scheduled Hours" column (2026-10-14, renamed from "CXHub
// Scheduled Hours" the same day) -- a plain decimal hour count, not a
// clock duration, so this is deliberately NOT
// formatHandleMs (m:ss/h:mm:ss) or the asa_hms-style H:MM:SS either; a
// supervisor scanning a column of these wants "38.5", not "38:30:00".
function formatMinutesAsHours(minutes) {
  if (typeof minutes !== 'number' || !isFinite(minutes)) return '—'
  return (minutes / 60).toFixed(1)
}

// One line of the message balance sheet. `emphasis` renders a subtotal/total
// (bold, with a top rule); `indent` nests a bucket under its subtotal.
function SheetRow({ label, value, emphasis, indent, muted, onToggle, expanded }) {
  return (
    <Stack
      direction="row"
      alignItems="baseline"
      onClick={onToggle}
      sx={{
        py: 0.35,
        pl: indent ? 3 : 0,
        cursor: onToggle ? 'pointer' : 'default',
        ...(emphasis && { borderTop: 1, borderColor: 'divider', mt: 0.25, pt: 0.6 }),
        ...(onToggle && { '&:hover': { color: 'text.primary' } }),
      }}
    >
      <Typography
        variant="body2"
        sx={{ flex: 1, fontWeight: emphasis ? 600 : 400, color: muted ? 'text.secondary' : 'text.primary' }}
      >
        {onToggle != null && (
          <Box component="span" sx={{ display: 'inline-block', width: 14, color: 'text.secondary' }}>
            {expanded ? '▾' : '▸'}
          </Box>
        )}
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: emphasis ? 700 : 500, color: muted ? 'text.secondary' : 'text.primary' }}
      >
        {fmtCount(value)}
      </Typography>
    </Stack>
  )
}

const PRESETS = [
  { value: 'thisWeek', label: 'This week' },
  { value: 'week', label: 'Last week' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'month', label: 'Last month' },
  { value: '30d', label: 'Past 30 days' },
]

const GRANULARITIES = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
]

const TILE_WIDTH = 253 // 220 * 1.15
// Narrower than TILE_WIDTH, used ONLY by the Live queue boxes row -- 6 boxes
// (as of the 1A Email addition, 2026-10-01) need to fit in one row without
// wrapping, which TILE_WIDTH's 253px wouldn't comfortably do.
const LIVE_QUEUE_TILE_WIDTH = 165
const TILE_GAP = 16 // Stack spacing={2} at the default 8px unit
const TILE_COUNT = 4
const CHART_WIDTH = TILE_COUNT * TILE_WIDTH + (TILE_COUNT - 1) * TILE_GAP
const SIDE_CHART_WIDTH = (CHART_WIDTH - TILE_GAP) / 2 // messages + response-time charts side by side
const SHEET_WIDTH = TILE_WIDTH * 2 + TILE_GAP // two sheets side by side == CHART_WIDTH

// One balance sheet. The same arrival cohort is reported in two units --
// messages and contacts (see repositories/reporting.js getEbayMessageReport) --
// and both render through here, so the two tables can't drift apart; `unit` is
// the noun for the total/valid lines. The superseded bucket shows only on the
// message sheet: dropping it IS the message-to-contact conversion, so it has
// no meaning in contacts.
function BalanceSheet({ title, unit, d, showSuperseded, caption }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <Paper variant="outlined" sx={{ p: 3, width: SHEET_WIDTH }}>
      <Typography variant="overline" color="text.secondary">{title}</Typography>
      <Box sx={{ mt: 1 }}>
        <SheetRow label={`Total ${unit}`} value={d.totalCount} emphasis />
        <SheetRow label={intentLabel('spam')} value={d.spamCount} indent muted />
        <SheetRow label={`Valid ${unit}`} value={d.validCount} emphasis />
        {/* System-resolved first: these were handled with no human touch, so
            the human count — the actual-labor metric being tracked — reads as
            what's left over. */}
        <SheetRow
          label="Resolved by system (no human)"
          value={d.systemCount}
          indent
          onToggle={() => setExpanded((v) => !v)}
          expanded={expanded}
        />
        <Collapse in={expanded} unmountOnExit>
          <Box sx={{ pl: 2 }}>
            <SheetRow label="Automated reply" value={d.automatedCount} indent muted />
            <SheetRow label="Auto-closed (no action needed)" value={d.autoClosedCount} indent muted />
            {showSuperseded && (
              <SheetRow label="Superseded (buyer replied first)" value={d.supersededCount} indent muted />
            )}
            <SheetRow label="Uncategorized (legacy)" value={d.legacyCount} indent muted />
          </Box>
        </Collapse>
        <SheetRow label="Resolved by a human" value={d.humanCount} indent />
        <SheetRow label="Still in queue (from this range)" value={d.queueCount} indent />
      </Box>
      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" color="text.secondary">{caption}</Typography>
    </Paper>
  )
}

// How far back Scorecards' own fetch window reaches (how much history the
// period multi-select below has to choose from) -- fixed, not a user
// control: the multi-select already lets a supervisor pick exactly which
// weeks/months they want, so a separate "N weeks/months back" toggle (as
// AHT_REP_PERIODS_OPTIONS has for AHT by account per rep) would just be a
// second, redundant way to narrow the same thing (Patrick's call, 2026-10-06).
const SCORECARDS_FETCH_PERIODS = { week: 26, month: 12 }

// The three Bright Pattern email cells (entered from Power BI) for one
// pooled row; a dash where no BP numbers were entered for the selection.
function BpEmailCells({ row, bold = false }) {
  const sx = { ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums', ...(bold ? { fontWeight: 700 } : {}) }
  const has = row.bpEmails > 0
  return (
    <>
      <TableCell align="right" sx={sx}>{has ? fmtCount(row.bpEmails) : '—'}</TableCell>
      <TableCell align="right" sx={sx}>{has ? formatHandleMs(row.bpEmailAht) : '—'}</TableCell>
      <TableCell align="right" sx={{ ...sx, fontWeight: 700 }}>{has ? formatHandleMs(row.combinedEmailAht) : '—'}</TableCell>
    </>
  )
}

// One Scorecards sub-table (Week or Month) -- a flat, pooled per-agent list
// (see scorecardsPooling.js's own module comment for why pooling, not a
// per-period breakdown), a Team/Agent filter identical in shape to AHT by
// account per rep's own, and a period multi-select standing in for that
// report's periods-back toggle (Patrick's explicit ask: pick any one or more
// of the fetched weeks/months, not just "show the last N"). A plain
// top-level function component (not nested in ReportingPage) so React never
// remounts it on an unrelated parent re-render.
function ScorecardTable({
  id, title, periodLabel, loading, error, data, pooled,
  selectedPeriods, setSelectedPeriods, selectedAgents, setSelectedAgents,
  exportFilename,
}) {
  const agentsPresent = data?.agents ?? []
  const periodOptions = data?.periods?.map((p) => p.label) ?? []
  const exportCsv = () => {
    const csv = buildScorecardsCsv({ agentRows: pooled.agentRows, total: pooled.total, hasSupportEmail: pooled.hasSupportEmail, hasBpEmail: pooled.hasBpEmail })
    downloadCsvText(csv, exportFilename)
  }

  return (
    <Box id={id} sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
      <SectionHeading variant="subtitle1" sx={{ mt: 4, mb: 1 }} title={title} />

      {loading ? (
        <CircularProgress size={24} />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : data ? (
        <>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 2, rowGap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{periodLabel}</InputLabel>
              <Select
                multiple
                value={selectedPeriods ?? []}
                label={periodLabel}
                onChange={(e) => setSelectedPeriods(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                renderValue={(selected) => selected.length === 1 ? selected[0] : `${selected.length} selected`}
              >
                {periodOptions.map((label) => (
                  <MenuItem key={label} value={label}>
                    <Checkbox size="small" checked={(selectedPeriods ?? []).includes(label)} />
                    {/* The still-in-progress bucket is flagged right in the
                        option label -- it's a real, selectable period (not
                        hidden), but its data is necessarily incomplete, so a
                        supervisor should know that before picking it. */}
                    <ListItemText primary={label === data?.currentPeriodLabel ? `${label} (current, in progress)` : label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Agents</InputLabel>
              <Select
                multiple
                value={selectedAgents ?? []}
                label="Agents"
                onChange={(e) => setSelectedAgents(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                renderValue={(selected) => selected.length === agentsPresent.length ? 'All' : `${selected.length} selected`}
              >
                {agentsPresent.map((agent) => (
                  <MenuItem key={agent} value={agent}>
                    <Checkbox size="small" checked={(selectedAgents ?? []).includes(agent)} />
                    <ListItemText primary={agent} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button size="small" onClick={() => setSelectedAgents(agentsPresent)}>Select all</Button>
            <Button size="small" onClick={() => setSelectedAgents([])}>Deselect all</Button>
            <Button size="small" variant="outlined" onClick={exportCsv}>Export to Excel</Button>
          </Stack>

          <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
            <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={ASA_CELL_BORDER}>Agent</TableCell>
                  <TableCell sx={ASA_CELL_BORDER} align="right">Total Emails</TableCell>
                  <TableCell sx={ASA_CELL_BORDER} align="right">Email AHT</TableCell>
                  {pooled.hasSupportEmail && <TableCell sx={ASA_CELL_BORDER} align="right">Support Email AHT</TableCell>}
                  {pooled.hasBpEmail && (
                    <>
                      <TableCell sx={ASA_CELL_BORDER} align="right">BP Emails</TableCell>
                      <TableCell sx={ASA_CELL_BORDER} align="right">BP Email AHT</TableCell>
                      <TableCell sx={{ ...ASA_CELL_BORDER, fontWeight: 700 }} align="right">Combined Email AHT</TableCell>
                    </>
                  )}
                  <TableCell sx={ASA_CELL_BORDER} align="right">ADH %</TableCell>
                  <TableCell sx={ASA_CELL_BORDER} align="right">Scheduled Hours</TableCell>
                  <TableCell sx={ASA_CELL_BORDER} align="right">Utilization %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pooled.agentRows.map((a) => (
                  <TableRow key={a.agent} hover>
                    <TableCell sx={ASA_CELL_BORDER}>{a.agent}</TableCell>
                    <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>{fmtCount(a.totalContacts)}</TableCell>
                    <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>{formatHandleMs(a.emailAht)}</TableCell>
                    {pooled.hasSupportEmail && (
                      <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                        {a.supportEmailContacts > 0 ? formatHandleMs(a.supportEmailAht) : '—'}
                      </TableCell>
                    )}
                    {pooled.hasBpEmail && <BpEmailCells row={a} />}
                    <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>{formatPercent(a.adh)}</TableCell>
                    <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>{formatMinutesAsHours(a.cxhubScheduledMinutes)}</TableCell>
                    <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>{formatPercent(a.utilization)}</TableCell>
                  </TableRow>
                ))}
                {pooled.total && (
                  <TableRow sx={{ borderTop: '2px solid', borderColor: 'divider' }}>
                    <TableCell sx={{ ...ASA_CELL_BORDER, fontWeight: 700 }}>Total</TableCell>
                    <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtCount(pooled.total.totalContacts)}</TableCell>
                    <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatHandleMs(pooled.total.emailAht)}</TableCell>
                    {pooled.hasSupportEmail && (
                      <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {pooled.total.supportEmailContacts > 0 ? formatHandleMs(pooled.total.supportEmailAht) : '—'}
                      </TableCell>
                    )}
                    {pooled.hasBpEmail && <BpEmailCells row={pooled.total} bold />}
                    <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatPercent(pooled.total.adh)}</TableCell>
                    <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatMinutesAsHours(pooled.total.cxhubScheduledMinutes)}</TableCell>
                    <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatPercent(pooled.total.utilization)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      ) : null}
    </Box>
  )
}

export default function ReportingPage({ onOpenInAgent, showForecastingTab = false }) {
  const theme = useTheme()
  // Account Reports is the everyday tab (default) -- Legacy Reports exists
  // as a home for sections that get moved out of the everyday view once
  // they're identified as "checked occasionally" rather than daily. Empty
  // for now; nothing has been moved into it yet.
  const [reportingTab, setReportingTab] = useState('account')
  const [preset, setPreset] = useState('30d')
  const [custom, setCustom] = useState({ from: '', to: '' })
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [metric, setMetric] = useState(null)
  const [selectedIntents, setSelectedIntents] = useState(null)
  const [selectedAgents, setSelectedAgents] = useState(null)
  const [selectedSpamRules, setSelectedSpamRules] = useState(null)
  const [spamRuleMetric, setSpamRuleMetric] = useState('count')
  const [selectedCompanies, setSelectedCompanies] = useState(DEFAULT_COMPANIES)
  const [selectedChannel, setSelectedChannel] = useState('all')

  // Contacts-per-hour, Contacts-per-hour-by-intent, Handle time, and Online
  // vs. active all used to carry their own independent date-range controls
  // (preset + custom from/to), each entirely disconnected from the others
  // and from Volume/Response time above -- picking "Last week" in one
  // section had zero effect on any other, which was the actual point of
  // confusion this was built to fix. All four now share the ONE `preset`/
  // `custom` state already driving Volume/Response time (see the fetch
  // effects below, and the single rendered control in the sticky header) --
  // no more per-section date pickers. AHT/ASA by account are the deliberate
  // exception: they keep their own independent "N weeks back" toggle (a
  // trailing-weeks trend table is a different kind of question than "what
  // date range am I looking at"), unaffected by any of this.
  const [cphData, setCphData] = useState(null)
  const [cphLoading, setCphLoading] = useState(true)
  const [cphError, setCphError] = useState(null)
  // Weekly by default -- per-agent efficiency review is typically done week
  // over week; daily/monthly are there to zoom in or out, not the default lens.
  const [cphGranularity, setCphGranularity] = useState('week')
  const [selectedCphAgents, setSelectedCphAgents] = useState(null)

  // Contacts per hour, by intent -- a sibling to the per-agent chart above,
  // grouped by intent instead of agent to compare which intents take longer
  // to handle. Shares the page's one master date filter (see above).
  const [cphiData, setCphiData] = useState(null)
  const [cphiLoading, setCphiLoading] = useState(true)
  const [cphiError, setCphiError] = useState(null)
  const [cphiGranularity, setCphiGranularity] = useState('week')
  const [selectedCphiIntents, setSelectedCphiIntents] = useState(null)

  // Handle time — how long a contact stays open with an agent. Shares the
  // page's one master date filter (see above). Median is the default lens,
  // not the mean: one contact left open over lunch moves the mean and hides
  // the actual shift.
  const [htData, setHtData] = useState(null)
  const [htLoading, setHtLoading] = useState(true)
  const [htError, setHtError] = useState(null)

  // Longest AHT contacts -- no default agent (nothing shown until one is
  // picked, see the section's own comment), so loading/error start false/null
  // rather than htData's true/null.
  const [longestAhtAgent, setLongestAhtAgent] = useState('')
  const [longestAhtData, setLongestAhtData] = useState(null)
  const [longestAhtLoading, setLongestAhtLoading] = useState(false)
  const [longestAhtError, setLongestAhtError] = useState(null)
  const [longestAhtShown, setLongestAhtShown] = useState(LONGEST_AHT_PAGE_SIZE)
  const [longestAhtSummary, setLongestAhtSummary] = useState(null)
  const [longestAhtSummaryLoading, setLongestAhtSummaryLoading] = useState(false)
  const [longestAhtSummaryError, setLongestAhtSummaryError] = useState(null)
  // "Look up a contact": one contact's claims, pauses and AHT sum, by number.
  const [timelineInput, setTimelineInput] = useState('')
  const [timeline, setTimeline] = useState(null)
  const [timelineLoading, setTimelineLoading] = useState(false)
  const [timelineError, setTimelineError] = useState(null)
  const lookUpContact = useCallback(async () => {
    const conv = timelineInput.trim()
    if (!/^[0-9]{1,12}$/.test(conv)) { setTimelineError('Enter the contact number, digits only.'); setTimeline(null); return }
    setTimelineLoading(true)
    setTimelineError(null)
    try {
      setTimeline(await fetchContactHandleTime(conv))
    } catch (e) {
      setTimeline(null)
      setTimelineError(/404/.test(e.message) ? `No contact numbered ${conv}.` : e.message)
    } finally {
      setTimelineLoading(false)
    }
  }, [timelineInput])

  const [htMetric, setHtMetric] = useState('avg')
  // Week by default -- the whole point of this view is reviewing handle
  // time week over week, not day-by-day noise.
  const [htGranularity, setHtGranularity] = useState('week')
  const [selectedHtAgents, setSelectedHtAgents] = useState(null)
  const htChartRef = useRef(null)

  // AHT by intent — per rep drill-down's Intent selector. Defaults to the
  // slowest intent (derived below, alongside htIntentsRanked) whenever this
  // is unset or points at an intent that dropped out of the current range.
  const [selectedAhtIntentDrilldown, setSelectedAhtIntentDrilldown] = useState(null)

  // ASA (average speed of answer) by account — deliberately independent of
  // the page's company filter and date-range controls (it always shows every
  // registered account; see fetchAsaByAccount's comment) and of the from/to
  // shape every other section here uses — this one is "how many weeks back,"
  // not an arbitrary range, since it's a fixed week-over-week table.
  const [asaWeeks, setAsaWeeks] = useState(4)
  const [asaData, setAsaData] = useState(null)
  const [asaLoading, setAsaLoading] = useState(true)
  const [asaError, setAsaError] = useState(null)

  // QA KPI Dashboard's blended AHT -- shares asaWeeks (not its own control),
  // so it stays comparable to the ASA table it sits next to. See
  // getBlendedEmailAhtReport's module comment for the account pool.
  const [qaKpiAhtData, setQaKpiAhtData] = useState(null)
  const [qaKpiAhtLoading, setQaKpiAhtLoading] = useState(true)
  const [qaKpiAhtError, setQaKpiAhtError] = useState(null)

  // QA KPI Dashboard's own "ASA by account" preview -- also shares asaWeeks,
  // but deliberately its OWN fetch rather than reusing asaData above (see
  // QA_KPI_ASA_ACCOUNTS's own comment): Patrick needs 1A Website/AM Website
  // SLA reported separately, which the Account Reports page's pooled
  // "1A/AM Email" numbers can't give him.
  const [qaKpiAsaData, setQaKpiAsaData] = useState(null)
  const [qaKpiAsaLoading, setQaKpiAsaLoading] = useState(true)
  const [qaKpiAsaError, setQaKpiAsaError] = useState(null)

  // AHT by account -- same independence from the page's company filter and
  // date-range controls as ASA above (see fetchAhtByAccount's comment).
  const [ahtWeeks, setAhtWeeks] = useState(4)
  const [ahtData, setAhtData] = useState(null)
  const [ahtLoading, setAhtLoading] = useState(true)
  const [ahtError, setAhtError] = useState(null)

  // AHT by account per rep -- same independence as AHT by account above,
  // plus its own granularity (day/week/month re-buckets AND re-sizes the
  // window, so unlike Handle Time's granularity toggle this always
  // re-fetches). The agent filter is deliberately NOT in this effect's deps
  // -- narrowing it is a client-side re-pool (see poolAgentRows), not a
  // re-fetch, same reasoning Handle Time's own agent selector follows.
  const [ahtRepGranularity, setAhtRepGranularity] = useState('week')
  const [ahtRepPeriods, setAhtRepPeriods] = useState(8)
  const [ahtRepData, setAhtRepData] = useState(null)
  const [ahtRepLoading, setAhtRepLoading] = useState(true)
  const [ahtRepError, setAhtRepError] = useState(null)
  const [selectedAhtRepAgents, setSelectedAhtRepAgents] = useState(null)
  // Collapsed period labels (client-side only, resets on refetch) -- a
  // period's own Total row still shows when collapsed, only the per-agent
  // rows hide, so the at-a-glance summary survives shrinking the view.
  const [collapsedAhtRepPeriods, setCollapsedAhtRepPeriods] = useState(() => new Set())
  const toggleAhtRepPeriod = useCallback((label) => {
    setCollapsedAhtRepPeriods((prev) => {
      const next = new Set(prev)
      if (next.has(label)) next.delete(label)
      else next.add(label)
      return next
    })
  }, [])

  // Scorecards tab -- a supervisor's per-agent email view, independent of
  // the page's shared company/date-range filter like every other
  // trailing-weeks report above. Fetches a generous, FIXED window up front
  // (SCORECARDS_FETCH_PERIODS -- same "fetch everything, let the UI slice
  // it" pattern AHT outliers/AHT-per-rep already use) so the period
  // multi-select below has real history to choose from; there is no
  // periods-back control to re-fetch on, since the multi-select already
  // covers that (Patrick's call, 2026-10-06). Week and Month are two fully
  // independent instances of the same shape (own data/loading/error, own
  // period selection, own team/agent filter) -- same reasoning the page's
  // other independent sections (ahtWeeks vs asaWeeks) already follow, not
  // one shared granularity toggle.
  const [scWeekData, setScWeekData] = useState(null)
  const [scWeekLoading, setScWeekLoading] = useState(true)
  const [scWeekError, setScWeekError] = useState(null)
  const [selectedScWeekPeriods, setSelectedScWeekPeriods] = useState(null)
  const [selectedScWeekAgents, setSelectedScWeekAgents] = useState(null)

  const [scMonthData, setScMonthData] = useState(null)
  const [scMonthLoading, setScMonthLoading] = useState(true)
  const [scMonthError, setScMonthError] = useState(null)
  const [selectedScMonthPeriods, setSelectedScMonthPeriods] = useState(null)
  const [selectedScMonthAgents, setSelectedScMonthAgents] = useState(null)

  // ONE Team filter driving both Week and Month at once (2026-10-08,
  // Patrick's ask -- each sub-table used to carry its own independent Team
  // chip row, so picking a team in Week had no effect on Month). Each table
  // still keeps its OWN Agents multi-select/Select-all/Deselect-all
  // underneath (that part wasn't asked to merge) -- picking a team here just
  // overwrites both tables' agent selections at once, each computed against
  // that table's OWN agent roster (team membership is a hand-copied
  // constant, so a name present in one table's window but not the other's
  // still resolves the same way).
  //
  // Multiple teams at once (2026-10-13, Patrick's ask -- it was one team or
  // every team, no way to pick exactly two). scSelectedTeams is an array of
  // team names, or null for "All" (every team) -- same shape and the same
  // toggle convention as AHT by account per rep's Team chips below:
  // clicking "All" always resets to everyone; the first individual-team
  // click narrows away from "All" to just that team; a later click adds or
  // removes that team; removing the last remaining team falls back to "All"
  // rather than leaving an empty selection.
  const [scSelectedTeams, setScSelectedTeams] = useState(null)
  const selectScorecardsTeam = useCallback((team) => {
    const next = team === null
      ? null
      : scSelectedTeams === null
        ? [team]
        : scSelectedTeams.includes(team)
          ? (scSelectedTeams.length === 1 ? null : scSelectedTeams.filter((t) => t !== team))
          : [...scSelectedTeams, team]
    setScSelectedTeams(next)
    const agentsFor = (present) => {
      if (next === null) return present
      const inSelectedTeams = new Set(next.flatMap((t) => agentsInTeam(present, t)))
      return present.filter((a) => inSelectedTeams.has(a))
    }
    setSelectedScWeekAgents(agentsFor(scWeekData?.agents ?? []))
    setSelectedScMonthAgents(agentsFor(scMonthData?.agents ?? []))
  }, [scWeekData, scMonthData, scSelectedTeams])

  // Automation Reports tab -- same independence from the page's shared
  // company/date-range filter as AHT/ASA by account (a trailing-weeks
  // trend table, not a date-range snapshot). Rate and failures share ONE
  // weeks-back control since they're two views of one question (why did
  // the rate move), not independent metrics.
  const [automationWeeks, setAutomationWeeks] = useState(8)
  const [automationData, setAutomationData] = useState(null)
  const [automationLoading, setAutomationLoading] = useState(true)
  const [automationError, setAutomationError] = useState(null)
  // Account-group filter (2026-10-07) -- every group selected by default;
  // narrowing it is a client-side re-pool (poolAutomationRate/Failures), not
  // a re-fetch, same reasoning every other filter on this page follows.
  const [selectedAutomationGroups, setSelectedAutomationGroups] = useState(AUTOMATION_TREND_GROUPS)
  const [automationSummary, setAutomationSummary] = useState(null)
  const [automationSummaryLoading, setAutomationSummaryLoading] = useState(false)
  const [automationSummaryError, setAutomationSummaryError] = useState(null)

  // Dashboard tab -- a "right now" snapshot (see DASHBOARD_POLL_MS above),
  // not a date-ranged report; independent of the page's shared filter and
  // of every other tab's own state. Two separate loading/error pairs so a
  // slow/failed read on one never blanks the other.
  const [dashboardAgents, setDashboardAgents] = useState(null)
  const [dashboardAgentsLoading, setDashboardAgentsLoading] = useState(true)
  const [dashboardAgentsError, setDashboardAgentsError] = useState(null)
  const [dashboardQueues, setDashboardQueues] = useState(null)
  const [dashboardQueuesError, setDashboardQueuesError] = useState(null)
  const [dashboardQueueDetail, setDashboardQueueDetail] = useState(null)

  // Weekly Report tab -- independent on-demand downloads, each with its own
  // busy/error pair so clicking one never disables or hides another.
  const [weeklyExportBusy, setWeeklyExportBusy] = useState(false)
  const [weeklyExportError, setWeeklyExportError] = useState(null)
  const [laborBusy, setLaborBusy] = useState(false)
  const [laborError, setLaborError] = useState(null)

  const handleDownloadWeeklyExport = useCallback(async () => {
    setWeeklyExportBusy(true)
    setWeeklyExportError(null)
    try {
      await downloadWeeklyExport()
    } catch (e) {
      setWeeklyExportError(`Download failed: ${e.message ?? e}`)
    } finally {
      setWeeklyExportBusy(false)
    }
  }, [])

  const handleDownloadLabor = useCallback(async () => {
    setLaborBusy(true)
    setLaborError(null)
    try {
      await downloadWeeklyLaborUtilization()
    } catch (e) {
      // downloadCsv()'s shared !res.ok check throws a plain "path -> status"
      // Error -- a 503 here means Schedule Hub reads are off in this
      // environment (not a bug), so that specific case gets its own message.
      setLaborError(/-> 503/.test(String(e.message)) ? 'Schedule Hub integration is disabled in this environment.' : `Download failed: ${e.message ?? e}`)
    } finally {
      setLaborBusy(false)
    }
  }, [])

  // Online vs. active -- shares the page's one master date filter (see
  // above). `companies` only narrows which activeHours count -- online
  // time itself isn't account-scoped (see fetchOnlineTime's comment).
  const [onlineData, setOnlineData] = useState(null)
  const [onlineLoading, setOnlineLoading] = useState(true)
  const [onlineError, setOnlineError] = useState(null)

  const load = useCallback(async (params) => {
    setLoading(true)
    setError(null)
    try {
      const result = await fetchReportingSummary(params)
      setData(result)
      // Default to the top N by volume each time a new range loads.
      setSelectedIntents((result.intents ?? []).slice(0, DEFAULT_INTENT_COUNT).map((i) => i.key))
      setSelectedAgents((result.agents ?? []).slice(0, DEFAULT_AGENT_COUNT).map((a) => a.key))
      // Default to active rules only — a retired rule can't produce new
      // matches, so its trend line would only ever be flat/historical noise.
      setSelectedSpamRules((result.spamRuleTrend ?? []).filter((r) => r.isActive).map((r) => r.key))
    } catch (e) {
      setError(e.message)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (preset) load({ preset, companies: selectedCompanies, channel: selectedChannel })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, selectedCompanies, selectedChannel, load])

  // Company changes while viewing a custom range reload with that same
  // range -- the preset effect above only covers the preset case.
  useEffect(() => {
    if (!preset && custom.from && custom.to) load({ from: custom.from, to: custom.to, companies: selectedCompanies, channel: selectedChannel })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanies, selectedChannel])

  const loadContactsPerHour = useCallback(async (params) => {
    setCphLoading(true)
    setCphError(null)
    try {
      const result = await fetchContactsPerHour(params)
      setCphData(result)
      // Default to every agent selected -- the point of this chart is the
      // overall efficiency trend, not spotlighting a top few.
      setSelectedCphAgents((result.agents ?? []).map((a) => a.key))
    } catch (e) {
      setCphError(e.message)
      setCphData(null)
    } finally {
      setCphLoading(false)
    }
  }, [])

  useEffect(() => {
    if (preset) loadContactsPerHour({ preset, companies: selectedCompanies, channel: selectedChannel })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, selectedCompanies, selectedChannel, loadContactsPerHour])

  useEffect(() => {
    if (!preset && custom.from && custom.to) loadContactsPerHour({ from: custom.from, to: custom.to, companies: selectedCompanies, channel: selectedChannel })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanies, selectedChannel])

  const loadContactsPerHourByIntent = useCallback(async (params) => {
    setCphiLoading(true)
    setCphiError(null)
    try {
      const result = await fetchContactsPerHourByIntent(params)
      setCphiData(result)
      setSelectedCphiIntents((result.intents ?? []).map((i) => i.key))
    } catch (e) {
      setCphiError(e.message)
      setCphiData(null)
    } finally {
      setCphiLoading(false)
    }
  }, [])

  useEffect(() => {
    if (preset) loadContactsPerHourByIntent({ preset, companies: selectedCompanies, channel: selectedChannel })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, selectedCompanies, selectedChannel, loadContactsPerHourByIntent])

  useEffect(() => {
    if (!preset && custom.from && custom.to) loadContactsPerHourByIntent({ from: custom.from, to: custom.to, companies: selectedCompanies, channel: selectedChannel })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanies, selectedChannel])

  const loadHandleTime = useCallback(async (params) => {
    setHtLoading(true)
    setHtError(null)
    try {
      const result = await fetchHandleTime(params)
      setHtData(result)
      // Default to every agent selected -- deselecting is an explicit,
      // deliberate action (e.g. excluding a skewing outlier), not the
      // starting state.
      setSelectedHtAgents((result.agentDaily ?? []).map((a) => a.key))
    } catch (e) {
      setHtError(e.message)
      setHtData(null)
    } finally {
      setHtLoading(false)
    }
  }, [])

  useEffect(() => {
    if (preset) loadHandleTime({ preset, companies: selectedCompanies, channel: selectedChannel })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, selectedCompanies, selectedChannel, loadHandleTime])

  useEffect(() => {
    if (!preset && custom.from && custom.to) loadHandleTime({ from: custom.from, to: custom.to, companies: selectedCompanies, channel: selectedChannel })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanies, selectedChannel])

  // Longest AHT contacts -- fetches only once an agent is picked (unlike
  // every loader above, which fires on mount). Resets the "load more" count
  // and clears any prior summary whenever the underlying scope changes, since
  // a stale summary describing a different agent/range would be wrong, not
  // just outdated.
  const loadLongestAhtContacts = useCallback(async (params) => {
    setLongestAhtLoading(true)
    setLongestAhtError(null)
    setLongestAhtShown(LONGEST_AHT_PAGE_SIZE)
    setLongestAhtSummary(null)
    setLongestAhtSummaryError(null)
    try {
      setLongestAhtData(await fetchLongestAhtContacts(params))
    } catch (e) {
      setLongestAhtError(e.message)
      setLongestAhtData(null)
    } finally {
      setLongestAhtLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!longestAhtAgent) return
    if (preset) loadLongestAhtContacts({ preset, companies: selectedCompanies, channel: selectedChannel, agent: longestAhtAgent })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, selectedCompanies, selectedChannel, longestAhtAgent, loadLongestAhtContacts])

  useEffect(() => {
    if (!longestAhtAgent || preset) return
    if (custom.from && custom.to) loadLongestAhtContacts({ from: custom.from, to: custom.to, companies: selectedCompanies, channel: selectedChannel, agent: longestAhtAgent })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanies, selectedChannel, longestAhtAgent])

  // On-demand only -- never auto-fires, since it's a paid model call. The
  // server does its own caching (services/longest-aht-summary.js); this just
  // forwards the exact scope it just showed so the summary can never drift
  // from what's on screen.
  const runLongestAhtSummary = useCallback(async (regenerate) => {
    if (!longestAhtData) return
    setLongestAhtSummaryLoading(true)
    setLongestAhtSummaryError(null)
    try {
      const result = await fetchLongestAhtSummary({
        agent: longestAhtAgent,
        from: longestAhtData.from,
        to: longestAhtData.to,
        companies: selectedCompanies, channel: selectedChannel,
        regenerate,
      })
      setLongestAhtSummary(result.summary ? result : null)
    } catch (e) {
      setLongestAhtSummaryError(e.message)
    } finally {
      setLongestAhtSummaryLoading(false)
    }
  }, [longestAhtData, longestAhtAgent, selectedCompanies, selectedChannel])

  useEffect(() => {
    setAsaLoading(true)
    setAsaError(null)
    fetchAsaByAccount({ weeks: asaWeeks })
      .then(setAsaData)
      .catch((e) => { setAsaError(e.message); setAsaData(null) })
      .finally(() => setAsaLoading(false))
  }, [asaWeeks])

  // Independent call (not bundled into the ASA fetch above) so a failure or
  // slow response in one never blanks the other -- same reasoning this page
  // already uses elsewhere (contacts-per-hour/live-agents/live-queues).
  useEffect(() => {
    setQaKpiAhtLoading(true)
    setQaKpiAhtError(null)
    fetchQaKpiBlendedAht({ weeks: asaWeeks })
      .then(setQaKpiAhtData)
      .catch((e) => { setQaKpiAhtError(e.message); setQaKpiAhtData(null) })
      .finally(() => setQaKpiAhtLoading(false))
  }, [asaWeeks])

  // Also independent, same reasoning -- and deliberately its own endpoint
  // (see QA_KPI_ASA_ACCOUNTS's own comment), not the asaData fetch above.
  useEffect(() => {
    setQaKpiAsaLoading(true)
    setQaKpiAsaError(null)
    fetchQaKpiAsaByAccount({ weeks: asaWeeks })
      .then(setQaKpiAsaData)
      .catch((e) => { setQaKpiAsaError(e.message); setQaKpiAsaData(null) })
      .finally(() => setQaKpiAsaLoading(false))
  }, [asaWeeks])


  useEffect(() => {
    setAhtLoading(true)
    setAhtError(null)
    fetchAhtByAccount({ weeks: ahtWeeks })
      .then(setAhtData)
      .catch((e) => { setAhtError(e.message); setAhtData(null) })
      .finally(() => setAhtLoading(false))
  }, [ahtWeeks])

  useEffect(() => {
    setAutomationLoading(true)
    setAutomationError(null)
    // A stale summary from the previous window would silently keep showing
    // once `weeks` changes -- clear it so the "Generate summary" button
    // starts fresh rather than displaying a review of a different window.
    setAutomationSummary(null)
    setAutomationSummaryError(null)
    fetchAutomationTrends({ weeks: automationWeeks })
      .then(setAutomationData)
      .catch((e) => { setAutomationError(e.message); setAutomationData(null) })
      .finally(() => setAutomationLoading(false))
  }, [automationWeeks])

  const generateAutomationSummary = useCallback((regenerate = false) => {
    setAutomationSummaryLoading(true)
    setAutomationSummaryError(null)
    fetchAutomationTrendsSummary({ weeks: automationWeeks, groups: selectedAutomationGroups, regenerate })
      .then(setAutomationSummary)
      .catch((e) => setAutomationSummaryError(e.message))
      .finally(() => setAutomationSummaryLoading(false))
  }, [automationWeeks, selectedAutomationGroups])

  // Same "clear a stale summary" reasoning as the weeks-change effect above
  // -- the account-group filter is part of the summary's own scope too.
  useEffect(() => {
    setAutomationSummary(null)
    setAutomationSummaryError(null)
  }, [selectedAutomationGroups])

  // Dashboard tab -- polls only while active (DASHBOARD_POLL_MS), same
  // "refetch immediately on regaining tab focus, clear on unmount/tab
  // switch" convention ScheduleWidget.jsx already uses for its own live
  // status widget, so a backgrounded browser tab doesn't keep polling
  // uselessly. Three independent fetches, each with its own loading/error
  // state, so one slow/failed read never blanks the other two; agents-
  // loading gates the FIRST render only (subsequent polls refresh in
  // place, same "don't collapse the tree" reasoning the page's master
  // fetch above already follows).
  useEffect(() => {
    if (reportingTab !== 'dashboard') return
    let alive = true
    function loadAgents() {
      fetchLiveAgents()
        .then((d) => { if (alive) { setDashboardAgents(d.agents); setDashboardAgentsError(null) } })
        .catch((e) => { if (alive) setDashboardAgentsError(e.message) })
        .finally(() => { if (alive) setDashboardAgentsLoading(false) })
    }
    function loadQueues() {
      fetchLiveQueues()
        .then((d) => { if (alive) { setDashboardQueues(d.queues); setDashboardQueueDetail(d.detail); setDashboardQueuesError(null) } })
        .catch((e) => { if (alive) setDashboardQueuesError(e.message) })
    }
    function loadAll() { loadAgents(); loadQueues() }

    loadAll()
    const timer = setInterval(loadAll, DASHBOARD_POLL_MS)
    const onVisible = () => { if (document.visibilityState === 'visible') loadAll() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      alive = false
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [reportingTab])

  // Changing granularity resets periods to THAT granularity's own default
  // (the old numeric value likely isn't even one of the new list's options,
  // e.g. switching week -> month while periods=26 makes no sense as "26
  // months back").
  function setAhtRepGranularityAndResetPeriods(g) {
    setAhtRepGranularity(g)
    setAhtRepPeriods(AHT_REP_PERIODS_OPTIONS[g][1]) // the 2nd option -- same "not the narrowest, not the widest" default AHT_REP_PERIODS_OPTIONS.week already used (8 of [4,8,13,26])
  }

  useEffect(() => {
    setAhtRepLoading(true)
    setAhtRepError(null)
    fetchAhtByAccountPerRep({ granularity: ahtRepGranularity, periods: ahtRepPeriods })
      .then((result) => {
        setAhtRepData(result)
        // Default to every agent selected -- same "deselecting is a
        // deliberate action" convention Handle Time's agent filter uses.
        setSelectedAhtRepAgents(result.agents ?? [])
      })
      .catch((e) => { setAhtRepError(e.message); setAhtRepData(null) })
      .finally(() => setAhtRepLoading(false))
  }, [ahtRepGranularity, ahtRepPeriods])

  useEffect(() => {
    // Gated to its own tab -- unlike this page's other report sections
    // (which fetch on every mount regardless of tab, each a single bounded
    // DB query), Scorecards' adherence step fires one Schedule Hub HTTP
    // call PER AGENT PER GRANULARITY (attachScorecardsAdherence in
    // admin-reporting.js). Firing that on every Reporting page load,
    // including for every tab that has nothing to do with Scorecards, was
    // adding real latency and an external-service dependency nobody asked
    // for (caught 2026-10-06, the day this shipped). `reportingTab` is in
    // the deps so switching INTO this tab re-runs the fetch.
    if (reportingTab !== 'scorecards') return
    setScWeekLoading(true)
    setScWeekError(null)
    fetchScorecards({ granularity: 'week', periods: SCORECARDS_FETCH_PERIODS.week })
      .then((result) => {
        setScWeekData(result)
        // Default to the PRIOR, full week -- never the current,
        // still-in-progress one (result.currentPeriodLabel), even though
        // it's now a selectable option (2026-10-06, Patrick's call: the
        // current week's data is known-incomplete, not what a supervisor
        // wants to see by default). periods is sorted most-recent-first, so
        // this is simply the entry right after the current one; falls back
        // to the most recent entry if the current week somehow isn't first
        // (shouldn't happen -- the route always reaches today) or there's
        // no prior week yet at all.
        const currentIdx = result.periods.findIndex((p) => p.label === result.currentPeriodLabel)
        const defaultPeriod = result.periods[currentIdx + 1] ?? result.periods[0]
        setSelectedScWeekPeriods(defaultPeriod ? [defaultPeriod.label] : [])
        setSelectedScWeekAgents(result.agents ?? [])
        setScSelectedTeams(null)
      })
      .catch((e) => { setScWeekError(e.message); setScWeekData(null) })
      .finally(() => setScWeekLoading(false))
  }, [reportingTab])

  useEffect(() => {
    // Gated the same way the Week effect just above is -- see its comment.
    if (reportingTab !== 'scorecards') return
    setScMonthLoading(true)
    setScMonthError(null)
    fetchScorecards({ granularity: 'month', periods: SCORECARDS_FETCH_PERIODS.month })
      .then((result) => {
        setScMonthData(result)
        // Default to the current (most recent/in-progress) month --
        // result.currentPeriodLabel, which is always periods[0] since the
        // route reaches today, but named explicitly rather than assumed.
        const defaultPeriod = result.periods.find((p) => p.label === result.currentPeriodLabel) ?? result.periods[0]
        setSelectedScMonthPeriods(defaultPeriod ? [defaultPeriod.label] : [])
        setSelectedScMonthAgents(result.agents ?? [])
      })
      .catch((e) => { setScMonthError(e.message); setScMonthData(null) })
      .finally(() => setScMonthLoading(false))
  }, [reportingTab])

  const loadOnlineTime = useCallback(async (params) => {
    setOnlineLoading(true)
    setOnlineError(null)
    try {
      setOnlineData(await fetchOnlineTime(params))
    } catch (e) {
      setOnlineError(e.message)
      setOnlineData(null)
    } finally {
      setOnlineLoading(false)
    }
  }, [])

  useEffect(() => {
    if (preset) loadOnlineTime({ preset, companies: selectedCompanies, channel: selectedChannel })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, selectedCompanies, selectedChannel, loadOnlineTime])

  useEffect(() => {
    if (!preset && custom.from && custom.to) loadOnlineTime({ from: custom.from, to: custom.to, companies: selectedCompanies, channel: selectedChannel })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanies, selectedChannel])

  // The ONE custom-range "Apply" button for the whole page (sticky header)
  // -- fires every section's load at once, now that they all share this
  // same preset/custom state. AHT/ASA by account are the deliberate
  // exception (their own independent week-count control, untouched here).
  const applyCustom = () => {
    if (!custom.from || !custom.to) return
    setPreset(null)
    const range = { from: custom.from, to: custom.to, companies: selectedCompanies, channel: selectedChannel }
    load(range)
    loadContactsPerHour(range)
    loadContactsPerHourByIntent(range)
    loadHandleTime(range)
    loadOnlineTime(range)
  }

  const onlineCoverageStartDate = onlineData?.onlineCoverageStartsAt ? onlineData.onlineCoverageStartsAt.slice(0, 10) : undefined

  // Sums each agent's per-day points across the loaded range -- the report
  // hands back day-level detail so a future chart can use it, but this
  // first pass is a plain range-summary table (see the plan's own note
  // that day-by-day charting here is a later UX call, not decided now).
  //
  // Days before onlineCoverageStartDate are excluded from BOTH sides of the
  // sum, not just left as an onlineHours-is-0 caveat -- a wide preset like
  // "Past 30 days" would otherwise sum weeks of real Active history against
  // only 1-2 real days of Online history, making Idle/Idle% compare two
  // wildly mismatched windows for the same "range." Dropping those days
  // entirely keeps Online/Active/Idle apples-to-apples: whatever the table
  // shows always reflects the SAME actual days for both sides.
  const onlineTotals = (onlineData?.agents ?? []).map((a) => {
    const days = Object.entries(a.points).filter(([day]) => !onlineCoverageStartDate || day >= onlineCoverageStartDate)
    const points = days.map(([, p]) => p)
    return {
      key: a.key,
      onlineHours: points.reduce((sum, p) => sum + p.onlineHours, 0),
      activeHours: points.reduce((sum, p) => sum + p.activeHours, 0),
      idleHours: points.reduce((sum, p) => sum + p.idleHours, 0),
    }
  })

  const htCoverageStartDate = htData?.coverageStartsAt ? htData.coverageStartsAt.slice(0, 10) : undefined
  const htAgentsData = htData?.agentDaily ?? []
  const activeHtAgents = selectedHtAgents ?? []

  const activeAhtRepAgents = selectedAhtRepAgents ?? []

  // Scorecards -- pools whichever periods are currently selected into ONE
  // flat per-agent table (see scorecardsPooling.js's own module comment for
  // why this happens client-side and why it's a pool, not a per-period
  // breakdown like AHT per rep's own table). Reused identically for Week and
  // Month below, just fed each section's own data/selection state.
  function scorecardRows(data, selectedPeriods, selectedAgents) {
    if (!data) return { agentRows: [], total: null, hasSupportEmail: false, hasBpEmail: false }
    const periods = selectedPeriods?.length ? selectedPeriods : []
    const flat = data.periods
      .filter((p) => periods.includes(p.label))
      .flatMap((p) => p.agents)
      .filter((a) => (selectedAgents ?? []).includes(a.agent))
    const agentRows = poolScorecardRows(flat).sort((a, b) => a.agent.localeCompare(b.agent))
    return {
      agentRows,
      total: poolScorecardTotal(agentRows),
      hasSupportEmail: agentRows.some((a) => a.supportEmailContacts > 0),
      hasBpEmail: agentRows.some((a) => a.bpEmails > 0),
    }
  }
  const activeScWeekAgents = selectedScWeekAgents ?? []
  const activeScWeekPeriods = selectedScWeekPeriods ?? []
  const scWeek = scorecardRows(scWeekData, activeScWeekPeriods, activeScWeekAgents)

  const activeScMonthAgents = selectedScMonthAgents ?? []
  const activeScMonthPeriods = selectedScMonthPeriods ?? []
  const scMonth = scorecardRows(scMonthData, activeScMonthPeriods, activeScMonthAgents)

  // Automation Reports' account-group filter -- pooled client-side (see
  // automationTrendPooling.js's own module comment), reacting instantly to
  // selectedAutomationGroups with no re-fetch.
  const pooledAutomationRate = poolAutomationRate(automationData?.rate, selectedAutomationGroups)
  const pooledAutomationFailures = poolAutomationFailures(automationData?.failures, selectedAutomationGroups)

  const htAggregated = aggregateHandleTime({
    dates: htData?.dates ?? [],
    agentDaily: htAgentsData,
    granularity: htGranularity,
    selectedAgents: activeHtAgents,
  })
  const htSeries = htAggregated.series.filter((s) => s.key === htMetric)
  // Feeds the 5 stat tiles below -- selection-aware, unlike htData.overall
  // (always every agent), so deselecting a skewing agent changes the
  // headline numbers, not just the chart line. This is the whole point of
  // the feature: htData.overall/htData.agents (the per-agent table) are
  // deliberately left untouched elsewhere on this page.
  const htPooled = pooledStats({ agentDaily: htAgentsData, selectedAgents: activeHtAgents, dates: htData?.dates ?? [] })
  // Feeds the per-agent table's totals row -- ALWAYS every agent, independent
  // of the chart's current selection (that table is deliberately unscoped by
  // the agent picker). A true pooled recompute, not an average of each
  // agent's own median/average/90th-pct/longest: those can't be averaged
  // into a correct team value any more than the stat tiles above could (see
  // htPooled's comment) -- median/p90 need the real pooled distribution, and
  // "longest" is the MAX across agents, not the average of their maxima.
  const htTeamStats = pooledStats({ agentDaily: htAgentsData, selectedAgents: htData?.agents?.map((a) => a.key) ?? [], dates: htData?.dates ?? [] })

  // AHT by intent -- the range-wide ranking already computed server-side
  // (getHandleTimeReport's `intents`), sorted slowest first for the "AHT by
  // intent" table and as the drill-down's intent list/default.
  const htIntentsRanked = [...(htData?.intents ?? [])].sort((a, b) => b.avgMs - a.avgMs)
  // Falls back to the slowest intent whenever nothing is explicitly selected,
  // or the selection no longer exists in a newly-loaded range -- same
  // "reset to a valid default" convention activeMetric uses below for the
  // legacy tab's chart metric.
  const ahtIntentDrilldownKey = htIntentsRanked.some((i) => i.key === selectedAhtIntentDrilldown)
    ? selectedAhtIntentDrilldown
    : htIntentsRanked[0]?.key
  const ahtIntentDrilldownRows = agentIntentWithBaseline({ agentIntent: htData?.agentIntent ?? [], intents: htData?.intents ?? [] })
    .filter((r) => r.intent === ahtIntentDrilldownKey)
    .sort((a, b) => b.avgMs - a.avgMs)
  const ahtOutlierRows = outlierRows({ agentIntent: htData?.agentIntent ?? [], intents: htData?.intents ?? [] })
  const ahtOutlierRowsShown = ahtOutlierRows.slice(0, AHT_OUTLIERS_LIMIT)

  const exportHtChartPng = () => {
    const svg = htChartRef.current?.querySelector('svg')
    if (!svg) return
    exportChartPng({
      svg,
      backgroundColor: theme.palette.background.paper,
      captionColor: theme.palette.text.secondary,
      fontFamily: theme.typography.fontFamily,
      caption: HT_NOT_A_PERFORMANCE_MEASURE,
      filename: `handle-time-${htMetric}-${htGranularity}.png`,
    })
  }

  const exportAhtRepCsv = () => {
    if (!ahtRepData) return
    const csv = buildAhtByAccountPerRepCsv({
      periods: ahtRepData.periods,
      activeAgents: activeAhtRepAgents,
      accountKeys: AHT_REP_ACCOUNTS,
      accountLabels: AHT_REP_ACCOUNT_LABELS,
    })
    downloadCsvText(csv, `aht-by-account-per-rep-${ahtRepGranularity}.csv`)
  }

  const coverageStartDate = cphData?.coverageStartsAt ? cphData.coverageStartsAt.slice(0, 10) : undefined
  const cphAgentsData = cphData?.agents ?? []
  const activeCphAgents = selectedCphAgents ?? []
  const cphAggregated = aggregateContactsPerHour({
    dates: cphData?.dates ?? [],
    series: cphAgentsData,
    granularity: cphGranularity,
  })
  const activeCphSeries = cphAggregated.series.filter((s) => activeCphAgents.includes(s.key))

  // Total labor-hours spent handling contacts in the current range, summed
  // across the currently-selected agents (so it matches whatever the chart
  // itself is showing) -- a flat sum of each agent's own merged active-claim
  // hours per day, which is granularity-independent (unaffected by the
  // Daily/Weekly/Monthly toggle, since bucketing never changes the total).
  const cphTotalActiveHours = cphAgentsData
    .filter((a) => activeCphAgents.includes(a.key))
    .reduce((sum, a) => sum + Object.values(a.points).reduce((s, p) => s + p.activeHours, 0), 0)

  const cphiCoverageStartDate = cphiData?.coverageStartsAt ? cphiData.coverageStartsAt.slice(0, 10) : undefined

  const cphiIntentsData = cphiData?.intents ?? []
  const activeCphiIntents = selectedCphiIntents ?? []
  const cphiAggregated = aggregateAhtByIntent({
    dates: cphiData?.dates ?? [],
    series: cphiIntentsData,
    granularity: cphiGranularity,
  })
  const activeCphiSeries = cphiAggregated.series
    .filter((s) => activeCphiIntents.includes(s.key))
    .map((s) => ({ ...s, label: intentLabel(s.key), color: intentHexColor(theme, s.key) }))

  // Metric to chart defaults to the first series returned, and resets if the
  // previously-selected metric no longer exists in a new response.
  const series = data?.series ?? []
  const activeMetric = series.some((s) => s.key === metric) ? metric : series[0]?.key
  const activeSeries = series.filter((s) => s.key === activeMetric)

  // Sorted by match count descending -- "which rules are doing the most/
  // least work" is the question this table answers, not matching-precedence
  // order (sort_order, what the admin Spam Rules table itself uses).
  const spamRulesByCount = [...(data?.spamRules ?? [])].sort((a, b) => b.count - a.count)

  const spamRuleTrendData = data?.spamRuleTrend ?? []
  const activeSpamRuleKeys = selectedSpamRules ?? []
  const activeSpamRuleSeries = spamRuleTrendData
    .filter((r) => activeSpamRuleKeys.includes(r.key))
    .map((r) => ({
      key: r.key,
      label: `${r.shortId} — ${r.name}`,
      points: spamRuleMetric === 'rate' ? r.ratePoints : r.points,
    }))

  const intents = data?.intents ?? []
  const activeIntents = selectedIntents ?? []
  const activeIntentSeries = intents
    .filter((i) => activeIntents.includes(i.key))
    .map((i) => ({ key: i.key, label: intentLabel(i.key), points: i.points, color: intentHexColor(theme, i.key) }))

  const agentsData = data?.agents ?? []
  const activeAgents = selectedAgents ?? []
  const activeAgentSeries = agentsData
    .filter((a) => activeAgents.includes(a.key))
    .map((a) => ({ key: a.key, label: a.key, points: a.points }))

  return (
    // No top padding on the scrolling container itself (px/pb only) -- a
    // sticky child's `top: 0` is measured against this container's PADDING
    // edge, not its true top. With top padding here, that offset re-adds
    // itself the moment the page actually scrolls and stickiness engages
    // (not just at rest), permanently reproducing a gap no margin trick on
    // the child can cancel. The sticky header below supplies its own pt
    // instead, as real padding inside its own painted background.
    <Box sx={{ flex: 1, px: 4, pb: 4, overflowY: 'auto' }}>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, pt: 2, bgcolor: 'background.default', pb: 1, mb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>Reporting</Typography>
        <Tabs value={reportingTab} onChange={(_e, v) => setReportingTab(v)} sx={{ minHeight: 36, mb: 1 }}>
          <Tab value="account" label="Account Reports" sx={{ minHeight: 36, py: 0.5 }} />
          <Tab value="automation" label="Automation Reports" sx={{ minHeight: 36, py: 0.5 }} />
          <Tab value="legacy" label="Legacy Reports" sx={{ minHeight: 36, py: 0.5 }} />
          <Tab value="dashboard" label="Dashboard" sx={{ minHeight: 36, py: 0.5 }} />
          <Tab value="weekly" label="Weekly Report" sx={{ minHeight: 36, py: 0.5 }} />
          <Tab value="qa-kpi" label="QA KPI Dashboard" sx={{ minHeight: 36, py: 0.5 }} />
          <Tab value="scorecards" label="Scorecards" sx={{ minHeight: 36, py: 0.5 }} />
          {showForecastingTab && <Tab value="forecasting" label="Forecasting" sx={{ minHeight: 36, py: 0.5 }} />}
        </Tabs>

        {/* Company/date-range controls -- every section on Account Reports,
            Automation Reports and Legacy Reports reads from the same `data`
            these drive, so the controls live outside the tab switch rather
            than only existing on one of them. Hidden on Dashboard: that tab
            is a live snapshot (15s auto-refresh, its own "not a historical
            report" note below), and reads none of this -- Company/date
            controls next to data that ignores them just invites clicking
            Apply and wondering why nothing changed. Hidden on Scorecards too,
            same reasoning as AHT/ASA by account's own independence -- each
            Scorecards sub-table owns its own period/team/agent filter.
            Hidden on Forecasting too -- still being designed, not wired to
            these controls (or any data) yet. */}
        {reportingTab !== 'dashboard' && reportingTab !== 'scorecards' && reportingTab !== 'forecasting' && (
        <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" sx={{ rowGap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Company</InputLabel>
            <Select
              multiple
              value={selectedCompanies}
              label="Company"
              onChange={(e) => setSelectedCompanies(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              renderValue={(selected) => selected.length === KNOWN_COMPANIES.length ? 'All' : selected.join(', ')}
            >
              {KNOWN_COMPANIES.map((c) => (
                <MenuItem key={c} value={c}>
                  <Checkbox size="small" checked={selectedCompanies.includes(c)} />
                  <ListItemText primary={c} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Channel</InputLabel>
            <Select value={selectedChannel} label="Channel" onChange={(e) => setSelectedChannel(e.target.value)} data-testid="reporting-channel-select">
              {CHANNEL_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>

          <ToggleButtonGroup size="small" exclusive value={preset} onChange={(_e, v) => { if (v) setPreset(v) }}>
            {PRESETS.map((p) => <ToggleButton key={p.value} value={p.value}>{p.label}</ToggleButton>)}
          </ToggleButtonGroup>

          {/* No `min` on either field -- Message/Contact Balance and most
              other sections here have no coverage-start concept at all, so
              capping the SHARED picker to whatever the claim-based sections
              (Handle Time, Contacts/hr, Contacts/hr-by-intent) or Online vs.
              Active happen to support was blocking every other section from
              a wider range unnecessarily. Each section that DOES have a
              coverage boundary already handles a too-early range gracefully
              on its own (an uncoveredCount note, or Online vs. Active's own
              day-level exclusion) rather than silently showing wrong data. */}
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
              value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} />
            <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
              value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} />
            <Button size="small" variant="outlined" onClick={applyCustom} disabled={!custom.from || !custom.to}>Apply</Button>
          </Stack>
        </Stack>
        )}

        {data && (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.75, rowGap: 0.25 }}>
            {(reportingTab === 'account' ? ACCOUNT_NAV_SECTIONS : reportingTab === 'automation' ? AUTOMATION_NAV_SECTIONS : reportingTab === 'dashboard' ? DASHBOARD_NAV_SECTIONS : reportingTab === 'weekly' ? WEEKLY_NAV_SECTIONS : reportingTab === 'qa-kpi' ? QA_KPI_NAV_SECTIONS : reportingTab === 'scorecards' ? SCORECARDS_NAV_SECTIONS : reportingTab === 'forecasting' ? FORECASTING_NAV_SECTIONS : LEGACY_NAV_SECTIONS).map(([href, label]) => (
              <Button key={href} size="small" href={href} sx={{ minWidth: 0, py: 0.25 }}>{label}</Button>
            ))}
          </Stack>
        )}
      </Box>

      {loading && !data ? (
        // Only the very FIRST load (before any data has ever arrived) shows
        // this full-page spinner. Once `data` exists, a range change re-runs
        // this same fetch (shared across the master filter now) but must
        // NOT collapse the whole section tree back to a bare spinner --
        // that unmounts everything, the page shrinks to a few pixels tall,
        // and the browser drops the scroll position, throwing the reader
        // back to the top of the page on every single range change. Each
        // section already shows its own inline loading state for its own
        // refresh, so leaving the tree mounted here is enough.
        <CircularProgress size={24} />
      ) : error && !data ? (
        <Alert severity="error">{error}</Alert>
      ) : data ? (
        <>
          {/* Name the timezone: every range here is whole Eastern calendar
              days, and "which day is this?" is otherwise unanswerable from the
              screen — the reason a partial "today" looked wrong before. */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {data.from} → {data.to} · Eastern time
          </Typography>

          {reportingTab === 'account' && (
          <>
          <Box id="section-volume" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="flex-start" sx={{ mb: 2, rowGap: 2 }}>
              {/* The same arrival cohort in two units, side by side. Both are
                  partitioned by current disposition so the buckets foot exactly
                  (total = spam + valid; valid = human + system + queue). The
                  contact sheet is the message sheet with the superseded bucket
                  dropped — see repositories/reporting.js getEbayMessageReport,
                  which derives both from one query. */}
              <BalanceSheet
                title={`Message balance — ${data.from} → ${data.to}`}
                unit="messages"
                d={data}
                showSuperseded
                caption={
                  <>
                    Every message that arrived in this range, bucketed by where it ended up — the columns foot to zero.
                    {' '}“Still in queue” here is this range’s unresolved arrivals, distinct from the live queue tile.
                  </>
                }
              />

              {data.contacts && (
                <BalanceSheet
                  title={`Contact balance — ${data.from} → ${data.to}`}
                  unit="contacts"
                  d={data.contacts}
                  caption={
                    <>
                      The same arrivals counted as <em>work</em>: one contact is one run of buyer messages that ended in a
                      single action. Three messages in a row before anyone acts is <strong>one</strong> contact; a reply and
                      then a “thanks” the AI closes is <strong>two</strong>. Counted on the day the run’s last message arrived.
                    </>
                  }
                />
              )}

              <Stack spacing={2} sx={{ ml: 4 }}>
                <Paper variant="outlined" sx={{ p: 3, width: TILE_WIDTH, textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary">Avg wait — human responded</Typography>
                  <Typography variant="h3">{formatDuration(data.humanAvgWaitHours)}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 3, width: TILE_WIDTH, textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary">Avg wait — overall</Typography>
                  <Typography variant="h3">{formatDuration(data.overallAvgWaitHours)}</Typography>
                </Paper>
              </Stack>
            </Stack>
          </Box>

          <Box id="section-response" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
              <Box>
                {series.length > 1 && (
                  <ToggleButtonGroup size="small" exclusive value={activeMetric}
                    onChange={(_e, v) => { if (v) setMetric(v) }} sx={{ mb: 1 }}>
                    {series.map((s) => <ToggleButton key={s.key} value={s.key}>{s.label}</ToggleButton>)}
                  </ToggleButtonGroup>
                )}

                <Paper variant="outlined" sx={{ p: 2, width: CHART_WIDTH }}>
                  <LineChart dates={data.dates} series={activeSeries} valueFmt={(v) => Math.round(v).toString()} height={260} />
                </Paper>
              </Box>

              <Box sx={{ ml: 4 }}>
                <Typography variant="body2" sx={{ mb: 1, height: 32, display: 'flex', alignItems: 'center' }}>Response time</Typography>

                <Paper variant="outlined" sx={{ p: 2, width: SIDE_CHART_WIDTH }}>
                  <LineChart dates={data.dates} series={data.waitSeries ?? []} valueFmt={formatDuration} height={260} />
                </Paper>
              </Box>
            </Stack>
          </Box>
          </>
          )}

          {reportingTab === 'legacy' && (
          <>
          <Box id="section-arrival" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mt: 4, mb: 1 }} title="Message arrival pattern">
              When non-spam messages arrive, by day and hour — for staffing/scheduling, not resolution timing.
            
            </SectionHeading>

            <Paper variant="outlined" sx={{ p: 2, width: CHART_WIDTH }}>
              <ArrivalHeatmap grid={data.arrivalPattern?.grid} maxCount={data.arrivalPattern?.maxCount ?? 0} timezone={data.arrivalPattern?.timezone} />
            </Paper>
          </Box>

          <Box id="section-intent" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mt: 4, mb: 1 }} title="By intent" />

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Intents</InputLabel>
                <Select
                  multiple
                  value={activeIntents}
                  label="Intents"
                  onChange={(e) => setSelectedIntents(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => selected.length === intents.length ? 'All' : `${selected.length} selected`}
                >
                  {intents.map((i) => (
                    <MenuItem key={i.key} value={i.key}>
                      <Checkbox size="small" checked={activeIntents.includes(i.key)} />
                      <ListItemText primary={`${intentLabel(i.key)} (${i.total})`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button size="small" onClick={() => setSelectedIntents(intents.map((i) => i.key))}>Select all</Button>
              <Button size="small" onClick={() => setSelectedIntents([])}>Deselect all</Button>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, width: CHART_WIDTH }}>
              <LineChart dates={data.dates} series={activeIntentSeries} valueFmt={(v) => Math.round(v).toString()} height={260} />
            </Paper>
          </Box>

          <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 5, mb: 1 }}>
            <Typography variant="overline" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Agent efficiency</Typography>
            <Box sx={{ flex: 1, borderBottom: 1, borderColor: 'divider' }} />
          </Stack>

          <Box id="section-agent" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mb: 1 }} title="By agent">
              Conversations handled — attributed to whoever most recently resolved each one (decision or manual close).
            
            </SectionHeading>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Agents</InputLabel>
              <Select
                multiple
                value={activeAgents}
                label="Agents"
                onChange={(e) => setSelectedAgents(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                renderValue={(selected) => selected.length === agentsData.length ? 'All' : `${selected.length} selected`}
              >
                {agentsData.map((a) => (
                  <MenuItem key={a.key} value={a.key}>
                    <Checkbox size="small" checked={activeAgents.includes(a.key)} />
                    <ListItemText primary={`${a.key} (${a.total})`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button size="small" onClick={() => setSelectedAgents(agentsData.map((a) => a.key))}>Select all</Button>
            <Button size="small" onClick={() => setSelectedAgents([])}>Deselect all</Button>
          </Stack>

            <Paper variant="outlined" sx={{ p: 2, width: CHART_WIDTH }}>
              <LineChart dates={data.dates} series={activeAgentSeries} valueFmt={(v) => Math.round(v).toString()} height={260} />
            </Paper>
          </Box>

          {/* Online vs. active -- how much of an agent's time connected to
              CXHub was spent actively working a claimed conversation, vs.
              online with nothing claimed. Moved here from Account Reports
              (2026-09-30, Patrick's call) -- still an agent-efficiency lens
              like By agent above, just not part of the account-scoped report
              set. A plain range-summary table, not a day-by-day chart -- see
              the plan's own note that finer-grained charting here is a later
              UX call. */}
          <Box id="section-online" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mt: 4, mb: 1 }} title="Online vs. active">
              Total time each agent was connected to CXHub ("online"), compared to their merged active-claim time from
              By agent above, over the selected range. Idle time is online time with nothing claimed -- floored at
              zero, since the two are measured independently and shouldn't disagree by much. Online time itself isn't
              scoped to an account; the company filter above only narrows which active time counts toward the
              comparison.
            
            </SectionHeading>

            {onlineLoading ? (
              <CircularProgress size={24} />
            ) : onlineError ? (
              <Alert severity="error">{onlineError}</Alert>
            ) : onlineData ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {onlineCoverageStartDate
                    ? `Online-time tracking began ${onlineCoverageStartDate} — days before that are excluded from this table entirely (from both Online and Active), so a wider preset never mixes weeks of Active history against only a day or two of real Online data.`
                    : 'No online-time data recorded yet — check back once agents have been connected to CXHub.'}
                </Typography>
                {onlineTotals.length > 0 ? (
                <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
                  <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={ASA_CELL_BORDER}>Agent</TableCell>
                        <TableCell sx={{ ...ASA_CELL_BORDER, width: 110 }} align="right">Online</TableCell>
                        <TableCell sx={{ ...ASA_CELL_BORDER, width: 110 }} align="right">Active</TableCell>
                        <TableCell sx={{ ...ASA_CELL_BORDER, width: 110 }} align="right">Idle</TableCell>
                        <TableCell sx={{ ...ASA_CELL_BORDER, width: 90 }} align="right">Idle %</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {onlineTotals.map((a) => (
                        <TableRow key={a.key} hover>
                          <TableCell sx={ASA_CELL_BORDER}>{a.key}</TableCell>
                          <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>{formatAsaHours(a.onlineHours)}</TableCell>
                          <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>{formatAsaHours(a.activeHours)}</TableCell>
                          <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>{formatAsaHours(a.idleHours)}</TableCell>
                          <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                            {a.onlineHours > 0 ? `${Math.round((a.idleHours / a.onlineHours) * 100)}%` : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
                ) : (
                  <Typography variant="body2" color="text.secondary">No online time recorded yet for this range.</Typography>
                )}
              </>
            ) : null}
          </Box>
          </>
          )}

          {reportingTab === 'account' && (
          <>
          {/* Handle time — the duration side of the same events the
              contacts-per-hour chart below counts. Deliberately NOT built on
              conversations.handle_time_ms (which ignores released_at and so
              over-reports); see getHandleTimeReport. */}
          <Box id="section-handle" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mt: 4, mb: 1 }} title="Handle time">
              How long a contact stays open with an agent — from the moment they claim it to the moment they act on it.
              Each contact keeps its own duration, so contacts worked side by side overlap: this is time per contact,
              not a total of hours worked.
            
            </SectionHeading>

            {htLoading ? (
              <CircularProgress size={24} />
            ) : htError ? (
              <Alert severity="error">{htError}</Alert>
            ) : htData ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {htCoverageStartDate
                    ? `Assignment history available from ${htCoverageStartDate} — a range that starts earlier is allowed, but contacts before that date have no claim on record.`
                    : 'No assignment history recorded yet — check back once agents have claimed and completed some conversations.'}
                  {htData.uncoveredCount > 0 &&
                    ` ${fmtCount(htData.uncoveredCount)} contacts in this range had no claim on record and are excluded rather than counted as zero.`}
                </Typography>

                <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="flex-start" sx={{ mb: 2, rowGap: 2 }}>
                  {/* Median leads: handle time has a long right tail, so the
                      mean sits well above where most contacts actually land.
                      Bound to htPooled (the CURRENT agent selection), not
                      htData.overall (always every agent) -- deselecting a
                      skewing agent below must move these numbers, since
                      that's the entire reason this selector exists. */}
                  <Paper variant="outlined" sx={{ p: 3, width: TILE_WIDTH, textAlign: 'center' }}>
                    <Typography variant="overline" color="text.secondary">Median handle time</Typography>
                    <Typography variant="h3">{htPooled.count ? formatHandleMs(htPooled.medianMs) : 'No data'}</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 3, width: TILE_WIDTH, textAlign: 'center' }}>
                    <Typography variant="overline" color="text.secondary">Average handle time</Typography>
                    <Typography variant="h3">{htPooled.count ? formatHandleMs(htPooled.avgMs) : 'No data'}</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 3, width: TILE_WIDTH, textAlign: 'center' }}>
                    <Typography variant="overline" color="text.secondary">90th percentile</Typography>
                    <Typography variant="h3">{htPooled.count ? formatHandleMs(htPooled.p90Ms) : 'No data'}</Typography>
                    <Typography variant="caption" color="text.secondary">1 in 10 takes longer than this</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 3, width: TILE_WIDTH, textAlign: 'center' }}>
                    <Typography variant="overline" color="text.secondary">Contacts measured</Typography>
                    <Typography variant="h3">{fmtCount(htPooled.count)}</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 3, width: TILE_WIDTH, textAlign: 'center' }}>
                    <Typography variant="overline" color="text.secondary">Total handle time</Typography>
                    <Typography variant="h3">{htPooled.count ? formatHandleMs(htPooled.totalMs) : 'No data'}</Typography>
                    <Typography variant="caption" color="text.secondary">summed across selected agents</Typography>
                  </Paper>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Agents</InputLabel>
                    <Select
                      multiple
                      value={activeHtAgents}
                      label="Agents"
                      onChange={(e) => setSelectedHtAgents(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                      renderValue={(selected) => selected.length === htAgentsData.length ? 'All' : `${selected.length} selected`}
                    >
                      {htAgentsData.map((a) => (
                        <MenuItem key={a.key} value={a.key}>
                          <Checkbox size="small" checked={activeHtAgents.includes(a.key)} />
                          <ListItemText primary={a.key} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button size="small" onClick={() => setSelectedHtAgents(htAgentsData.map((a) => a.key))}>Select all</Button>
                  <Button size="small" onClick={() => setSelectedHtAgents([])}>Deselect all</Button>

                  <ToggleButtonGroup size="small" exclusive value={htGranularity}
                    onChange={(_e, v) => { if (v) setHtGranularity(v) }}>
                    {HT_GRANULARITIES.map((g) => <ToggleButton key={g.value} value={g.value}>{g.label}</ToggleButton>)}
                  </ToggleButtonGroup>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                  <ToggleButtonGroup size="small" exclusive value={htMetric}
                    onChange={(_e, v) => { if (v) setHtMetric(v) }}>
                    {htAggregated.series.map((s) => <ToggleButton key={s.key} value={s.key}>{s.label}</ToggleButton>)}
                  </ToggleButtonGroup>
                  <Button size="small" variant="outlined" onClick={exportHtChartPng}>Export PNG</Button>
                </Stack>

                <Paper variant="outlined" sx={{ p: 2, width: CHART_WIDTH }} ref={htChartRef}>
                  <LineChart dates={htAggregated.bucketDates} series={htSeries}
                    valueFmt={(v) => formatHandleMs(v * 60000)} height={260} showValueLabels />
                </Paper>

                {(htData.agents ?? []).length > 0 && (
                  <Paper variant="outlined" sx={{ mt: 2, width: CHART_WIDTH, overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Agent</TableCell>
                          <TableCell sx={{ width: 110 }} align="right">Contacts</TableCell>
                          <TableCell sx={{ width: 110 }} align="right">Median</TableCell>
                          <TableCell sx={{ width: 110 }} align="right">Average</TableCell>
                          <TableCell sx={{ width: 110 }} align="right">90th pct</TableCell>
                          <TableCell sx={{ width: 110 }} align="right">Longest</TableCell>
                          <TableCell sx={{ width: 110 }} align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {htData.agents.map((a) => (
                          <TableRow key={a.key} hover>
                            <TableCell>{a.key}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtCount(a.count)}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatHandleMs(a.medianMs)}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatHandleMs(a.avgMs)}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatHandleMs(a.p90Ms)}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>{formatHandleMs(a.maxMs)}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatHandleMs(a.totalMs)}</TableCell>
                          </TableRow>
                        ))}
                        {/* Totals row: a true pooled recompute across every agent
                            (htTeamStats), NOT an average of each agent's own
                            median/average/90th-pct/longest -- those can't be
                            averaged into a correct team value (a team median needs
                            the real pooled distribution, and "longest" is the MAX
                            across agents, not the average of their maxima). See
                            htTeamStats' own comment above. */}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                            {fmtCount(htTeamStats.count)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                            {formatHandleMs(htTeamStats.medianMs)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                            {formatHandleMs(htTeamStats.avgMs)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                            {formatHandleMs(htTeamStats.p90Ms)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                            {formatHandleMs(htTeamStats.maxMs)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                            {formatHandleMs(htTeamStats.totalMs)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Paper>
                )}
              </>
            ) : null}
          </Box>

          {/* Longest AHT contacts -- the per-contact drill-down behind Handle
              time's "Longest" stat just above: which specific contacts ran
              long, and (via the Paused column) whether that's genuinely hard
              work or a claim left open over a break. Unlike every other
              section on this page, this one needs an agent picked before it
              shows anything -- there's no "everyone" view, on purpose (a
              cross-agent longest-contacts list would just be dominated by
              whoever has the most volume, not who has the real outliers). */}
          <Box id="section-longest-aht" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mt: 4, mb: 1 }} title="Longest AHT contacts">
              One agent's longest-running contacts over the selected range, net of any confirmed break/inactivity
              pause (same AHT definition as everywhere else on this page) — for investigating individual outliers,
              not just averages. A Paused column appears only when a contact had confirmed pause time, to separate
              "claimed it, then stepped away" from genuinely long work.
            
            </SectionHeading>

            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, maxWidth: 980 }}>
              <SectionHeading variant="subtitle2" appearance="quiet" sx={{ mb: 0.5 }} title="Look up a contact">
                Type a contact number to see who held it and when, every pause inside the claim, and the same sum
                the reports make for each decision or close: time held − pauses that count − parked time. A pause
                marked "Away" is a stretch where the page went silent for over 15 minutes and then came back.
              
              </SectionHeading>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: timeline || timelineError ? 1.5 : 0 }}>
                <TextField
                  size="small"
                  label="Contact number"
                  value={timelineInput}
                  onChange={(e) => setTimelineInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') lookUpContact() }}
                  inputProps={{ inputMode: 'numeric' }}
                />
                <Button size="small" variant="outlined" onClick={lookUpContact} disabled={timelineLoading}>Show claims and pauses</Button>
                {timelineLoading && <CircularProgress size={18} />}
              </Stack>
              {timelineError && <Alert severity="warning">{timelineError}</Alert>}
              {timeline && (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Contact {timeline.convNumber}, {timeline.account}, {timeline.channel}, {timeline.status}.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Who held it</Typography>
                  <Paper variant="outlined" sx={{ overflowX: 'auto', mb: 1.5 }}>
                    <Table size="small" sx={{ borderCollapse: 'collapse' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={ASA_CELL_BORDER}>Person</TableCell>
                          <TableCell sx={ASA_CELL_BORDER}>Claimed</TableCell>
                          <TableCell sx={ASA_CELL_BORDER}>Ended</TableCell>
                          <TableCell sx={ASA_CELL_BORDER}>Pauses inside the claim</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {timeline.claims.length === 0 ? (
                          <TableRow><TableCell sx={ASA_CELL_BORDER} colSpan={4}>No claims recorded for this contact.</TableCell></TableRow>
                        ) : timeline.claims.map((c) => (
                          <TableRow key={c.id}>
                            <TableCell sx={ASA_CELL_BORDER}>{c.agent}</TableCell>
                            <TableCell sx={ASA_CELL_BORDER}>{formatEt(c.claimedAt)}</TableCell>
                            <TableCell sx={ASA_CELL_BORDER}>{c.releasedAt ? `${formatEt(c.releasedAt)} · ${releaseLabel(c.releasedReason)}` : releaseLabel(null)}</TableCell>
                            <TableCell sx={ASA_CELL_BORDER}>
                              {c.pauses.length === 0 && c.parks.length === 0 ? 'None' : null}
                              {c.pauses.map((p, i) => (
                                <Box key={`p${i}`} sx={{ color: p.subtracted ? undefined : 'text.secondary' }}>
                                  {formatEt(p.pausedAt)} → {p.resumedAt ? formatEt(p.resumedAt) : 'open'} ({formatMinutes(p.minutes)}): {pauseLabel(p.resolution)}
                                  {p.subtracted ? ' — counted' : ' — not counted'}
                                </Box>
                              ))}
                              {c.parks.map((p, i) => (
                                <Box key={`k${i}`} sx={{ color: 'text.secondary' }}>
                                  Parked {formatEt(p.parkedAt)} → {p.unparkedAt ? formatEt(p.unparkedAt) : 'still parked'}
                                </Box>
                              ))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                  <Typography variant="caption" color="text.secondary">What the reports make of it</Typography>
                  <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ borderCollapse: 'collapse' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={ASA_CELL_BORDER}>When</TableCell>
                          <TableCell sx={ASA_CELL_BORDER}>Person</TableCell>
                          <TableCell sx={ASA_CELL_BORDER}>What</TableCell>
                          <TableCell sx={ASA_CELL_BORDER} align="right">Held</TableCell>
                          <TableCell sx={ASA_CELL_BORDER} align="right">− Paused</TableCell>
                          <TableCell sx={ASA_CELL_BORDER} align="right">− Parked</TableCell>
                          <TableCell sx={ASA_CELL_BORDER} align="right">= AHT</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {timeline.events.length === 0 ? (
                          <TableRow><TableCell sx={ASA_CELL_BORDER} colSpan={7}>No decision or close recorded yet, so there is no AHT to report.</TableCell></TableRow>
                        ) : timeline.events.map((e, i) => (
                          <Fragment key={i}>
                            <TableRow>
                              <TableCell sx={ASA_CELL_BORDER}>{formatEt(e.at)}</TableCell>
                              <TableCell sx={ASA_CELL_BORDER}>{e.agent}</TableCell>
                              <TableCell sx={ASA_CELL_BORDER}>{eventLabel(e)}</TableCell>
                              <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>{formatHandleMs(e.heldMs)}</TableCell>
                              <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>{formatHandleMs(e.pausedMs)}</TableCell>
                              <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>{formatHandleMs(e.parkedMs)}</TableCell>
                              <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatHandleMs(e.ahtMs)}</TableCell>
                            </TableRow>
                            {e.note && (
                              <TableRow><TableCell sx={ASA_CELL_BORDER} colSpan={7}><Typography variant="caption" color="text.secondary">{e.note}</Typography></TableCell></TableRow>
                            )}
                          </Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </>
              )}
            </Paper>

            <FormControl size="small" sx={{ minWidth: 220, mb: 1 }}>
              <InputLabel>Agent</InputLabel>
              <Select
                value={longestAhtAgent}
                label="Agent"
                onChange={(e) => setLongestAhtAgent(e.target.value)}
              >
                <MenuItem value=""><em>Select an agent</em></MenuItem>
                {agentsData.map((a) => (
                  <MenuItem key={a.key} value={a.key}>{a.key}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {!longestAhtAgent ? null : longestAhtLoading ? (
              <CircularProgress size={24} />
            ) : longestAhtError ? (
              <Alert severity="error">{longestAhtError}</Alert>
            ) : longestAhtData ? (
              longestAhtData.contacts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No contacts for {longestAhtAgent} in this range.</Typography>
              ) : (
                <>
                  <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%', mb: 1.5 }}>
                    <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={ASA_CELL_BORDER}>Contact</TableCell>
                          <TableCell sx={ASA_CELL_BORDER}>Date completed</TableCell>
                          <TableCell sx={ASA_CELL_BORDER}>Intent</TableCell>
                          <TableCell sx={ASA_CELL_BORDER} align="right">AHT</TableCell>
                          <TableCell sx={ASA_CELL_BORDER} align="right">Paused</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {longestAhtData.contacts.slice(0, longestAhtShown).map((c, i) => (
                          <TableRow key={`${c.convNumber ?? c.externalConversationId ?? i}-${i}`} hover>
                            <TableCell sx={ASA_CELL_BORDER}>
                              {c.convNumber != null ? (
                                <Button
                                  size="small"
                                  onClick={() => onOpenInAgent?.(null, c.convNumber)}
                                  sx={{ minWidth: 0, p: 0, textTransform: 'none' }}
                                >
                                  {c.convNumber}
                                </Button>
                              ) : (c.externalConversationId ?? '—')}
                            </TableCell>
                            <TableCell sx={ASA_CELL_BORDER}>{new Date(c.completedAt).toLocaleString()}</TableCell>
                            <TableCell sx={ASA_CELL_BORDER}>{c.intent ? intentLabel(c.intent) : '—'}</TableCell>
                            <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>{formatHandleMs(c.ahtMs)}</TableCell>
                            <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                              {c.pausedMs > 0 ? formatHandleMs(c.pausedMs) : ''}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>

                  {longestAhtData.contacts.length > longestAhtShown && (
                    <Button size="small" onClick={() => setLongestAhtShown((n) => n + LONGEST_AHT_PAGE_SIZE)} sx={{ mb: 2 }}>
                      Load {Math.min(LONGEST_AHT_PAGE_SIZE, longestAhtData.contacts.length - longestAhtShown)} more
                      (showing {longestAhtShown} of {longestAhtData.contacts.length})
                    </Button>
                  )}

                  {/* AI summary -- see services/longest-aht-summary.js. Nothing
                      auto-generates: it's a paid model call, triggered only on
                      request, cached server-side until Regenerate. */}
                  <Paper variant="outlined" sx={{ p: 2, maxWidth: CHART_WIDTH }}>
                    {!longestAhtSummary && !longestAhtSummaryLoading ? (
                      <Button size="small" variant="outlined" onClick={() => runLongestAhtSummary(false)}>
                        Generate summary
                      </Button>
                    ) : longestAhtSummaryLoading ? (
                      <CircularProgress size={20} />
                    ) : longestAhtSummaryError ? (
                      <Alert severity="error">{longestAhtSummaryError}</Alert>
                    ) : longestAhtSummary ? (
                      <>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{longestAhtSummary.summary}</Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {longestAhtSummary.cached ? 'Cached · ' : ''}{longestAhtSummary.model}
                            {longestAhtSummary.cost?.priced ? ` · $${longestAhtSummary.cost.usd.toFixed(4)}` : longestAhtSummary.cost ? ' · cost unknown' : ''}
                          </Typography>
                          <Button size="small" onClick={() => runLongestAhtSummary(true)}>Regenerate</Button>
                        </Stack>
                      </>
                    ) : null}
                  </Paper>
                </>
              )
            ) : null}
          </Box>

          {/* AHT by account -- moved to sit right after Handle time (both are
              "how long does handling take," just sliced differently: by
              agent above, by account here) rather than after ASA, which
              measures a different thing (speed of answer, not handle time).
              Same account-grouped, week-ending-Saturday shape as ASA below,
              but measuring the claim-to-action span getHandleTimeReport
              measures (see that function's module comment), grouped by
              account instead of by agent. No SLA/target column: no AHT
              target is defined anywhere in the app today, unlike ASA's
              slaTargetHours. */}
          <Box id="section-aht" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mt: 4, mb: 1 }} title="AHT by account">
              Average handle time per account, in Sunday–Saturday weeks labeled by their ending Saturday, with
              median and total contacts shown underneath each average — a handful of contacts left claimed while
              an agent works elsewhere can pull an account's average well above what's typical that week; the
              median isn't swayed by a single long outlier the way the average is, and the contacts count is the
              denominator behind both. The bottom "Total (all accounts)" row is a true pooled average across
              every account (sum of everyone's time divided by sum of everyone's contacts, that week) — not an
              average of the account rows' own averages, which would weight a small account the same as a big one.
              Shows every registered account with data in this window, regardless of the company filter above.
              A "—" cell means no contacts were resolved that week for that account, not a zero.
              "1A Email" and "AM Email" each combine the brand's Website and Internal-notice mailboxes into one row.
              "Support Email" pools every contact routed through the Support team rather than a brand's own
              Website/Internal traffic: 1A's Sales Hold Callback traffic (split out of 1A Email), the 1A Support
              mailbox (1ainternal@1aauto.com), and both brands' Customer Support mailboxes — reporting-only, not
              a single real registered account. TEST_EMAIL (the Lab-only shadow mailbox) is excluded entirely —
              its contacts are dev/QA activity, not live, agent-handled work.
            
            </SectionHeading>

            <ToggleButtonGroup size="small" exclusive value={ahtWeeks}
              onChange={(_e, v) => { if (v) setAhtWeeks(v) }} sx={{ mb: 1 }}>
              {ASA_WEEK_OPTIONS.map((w) => <ToggleButton key={w} value={w}>{w} weeks</ToggleButton>)}
            </ToggleButtonGroup>

            {ahtLoading ? (
              <CircularProgress size={24} />
            ) : ahtError ? (
              <Alert severity="error">{ahtError}</Alert>
            ) : ahtData ? (
              <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
                <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={ASA_CELL_BORDER}>Account</TableCell>
                      {ahtData.weekLabels.map((w) => (
                        <TableCell key={w} sx={{ ...ASA_CELL_BORDER, width: 110 }} align="right">{w}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ahtData.accounts
                      .filter((a) => Object.values(a.points).some((v) => v != null))
                      .map((a) => (
                        <Fragment key={a.account}>
                          <TableRow hover>
                            <TableCell sx={ASA_CELL_BORDER}>
                              {a.label ? (
                                // Not a plain registered account -- either Support Email
                                // (Sales Hold Callback traffic, the 1A Support mailbox, and both
                                // brands' Customer Support mailboxes, all pooled -- see
                                // reportingAccountKey's own comment) or a pooled group like AM
                                // Email (AM_WEBSITE + AM_INTERNAL_EMAIL combined -- see
                                // getAhtByAccountWithSupportEmailReport's own comment). Its own
                                // pre-built label, not the company/channel rendering below.
                                a.label
                              ) : (
                                <>
                                  {a.company} {CHANNEL_LABELS[a.channel] ?? a.channel}
                                  {/* Email specifically registers more than one account under
                                      the same company+channel (e.g. 1A's Website-client real
                                      account vs its Internal-client failure-notice mailbox) --
                                      channel alone renders identically for both, so the client
                                      is appended to actually tell them apart. eBay has no such
                                      ambiguity (one account per company), so it stays unlabeled. */}
                                  {a.channel === 'm365_email' && a.client && ` · ${a.client}`}
                                </>
                              )}
                            </TableCell>
                            {ahtData.weekLabels.map((w) => (
                              <TableCell key={w} align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                                {formatHandleMs(a.points[w])}
                              </TableCell>
                            ))}
                          </TableRow>
                          {/* Median row, directly under its account's average
                              row -- additive, never replacing the average
                              (see this section's own intro text above). */}
                          <TableRow hover>
                            <TableCell sx={{ ...ASA_CELL_BORDER, pl: 3 }}>
                              <Typography variant="caption" color="text.secondary">median</Typography>
                            </TableCell>
                            {ahtData.weekLabels.map((w) => (
                              <TableCell key={w} align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                                <Typography variant="caption" color="text.secondary">{formatHandleMs(a.medians?.[w])}</Typography>
                              </TableCell>
                            ))}
                          </TableRow>
                          {/* Total contacts -- the denominator behind the average/median
                              above, so a reader can tell a 1:57 average backed by 3
                              contacts from one backed by 300. Same "—" (not 0) rule as
                              points/medians for a week with no contacts. */}
                          <TableRow hover>
                            <TableCell sx={{ ...ASA_CELL_BORDER, pl: 3 }}>
                              <Typography variant="caption" color="text.secondary">total contacts</Typography>
                            </TableCell>
                            {ahtData.weekLabels.map((w) => (
                              <TableCell key={w} align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                                <Typography variant="caption" color="text.secondary">
                                  {a.counts?.[w] != null ? fmtCount(a.counts[w]) : '—'}
                                </Typography>
                              </TableCell>
                            ))}
                          </TableRow>
                        </Fragment>
                      ))}
                    {/* Bottom summary: a genuinely POOLED average across every
                        account (sum of every account's ms / sum of every
                        account's contacts, from ahtData.total) -- not an
                        average of the per-account averages above, which would
                        misweight a low-volume account the same as a
                        high-volume one. */}
                    <TableRow sx={{ borderTop: '2px solid', borderColor: 'divider' }}>
                      <TableCell sx={{ ...ASA_CELL_BORDER, fontWeight: 700 }}>Total (all accounts)</TableCell>
                      {ahtData.weekLabels.map((w) => (
                        <TableCell key={w} align="right" sx={{ ...ASA_CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                          {formatHandleMs(ahtData.total?.points?.[w])}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow hover>
                      <TableCell sx={{ ...ASA_CELL_BORDER, pl: 3 }}>
                        <Typography variant="caption" color="text.secondary">total contacts</Typography>
                      </TableCell>
                      {ahtData.weekLabels.map((w) => (
                        <TableCell key={w} align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                          <Typography variant="caption" color="text.secondary">
                            {ahtData.total?.counts?.[w] != null ? fmtCount(ahtData.total.counts[w]) : '—'}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            ) : null}
          </Box>

          {/* ASA by account — deliberately independent of the page's company
              filter and date-range controls above: this always shows every
              registered account (company x channel), regardless of what's
              selected elsewhere, so new channels appear automatically with no
              page changes. See fetchAsaByAccount/getAsaByAccountReport.
              Kept directly below AHT by account -- both are the same
              account-grouped, week-ending-Saturday shape, just measuring a
              different thing (speed of answer vs. handle time). */}
          <Box id="section-asa" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mt: 4, mb: 1 }} title="ASA by account">
              Average speed of answer per account, in Sunday–Saturday weeks labeled by their ending Saturday.
              Shows every registered account with data in this window, regardless of the company filter above.
              A "—" cell means no contacts were resolved that week for that account, not a zero.
              "1A Email" and "AM Email" each combine the brand's Website and Internal-notice mailboxes into one
              row, with the Internal mailbox's SLA target (1A 12h, AM 24h).
              "Support Email" pools every contact routed through the Support team rather than a brand's own
              Website/Internal traffic: 1A's Sales Hold Callback traffic (split out of 1A Email), the 1A Support
              mailbox (1ainternal@1aauto.com), and both brands' Customer Support mailboxes — reporting-only, not
              a single real registered account; no SLA target yet. TEST_EMAIL (the Lab-only shadow mailbox) is
              excluded entirely — its contacts are dev/QA activity, not live, agent-handled work.
            
            </SectionHeading>

            <ToggleButtonGroup size="small" exclusive value={asaWeeks}
              onChange={(_e, v) => { if (v) setAsaWeeks(v) }} sx={{ mb: 1 }}>
              {ASA_WEEK_OPTIONS.map((w) => <ToggleButton key={w} value={w}>{w} weeks</ToggleButton>)}
            </ToggleButtonGroup>

            {asaLoading ? (
              <CircularProgress size={24} />
            ) : asaError ? (
              <Alert severity="error">{asaError}</Alert>
            ) : asaData ? (
              <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
                <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={ASA_CELL_BORDER}>Account</TableCell>
                      {asaData.weekLabels.map((w) => (
                        <TableCell key={w} sx={{ ...ASA_CELL_BORDER, width: 110 }} align="right">{w}</TableCell>
                      ))}
                      <TableCell sx={{ ...ASA_CELL_BORDER, width: 110 }} align="right">SLA target</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* An account with no data anywhere in this window isn't shown yet -- a
                        registered-but-not-yet-live channel (e.g. email) shouldn't clutter the
                        table with an all-dashes row; it appears on its own once it has traffic. */}
                    {asaData.accounts
                      // Walmart (2026-10-28) is always listed, a row Patrick asked for by name, so a dash means no contacts yet
                      .filter((a) => a.account === 'WALMART' || Object.values(a.points).some((v) => v != null))
                      .map((a) => (
                        <TableRow key={a.account} hover>
                          <TableCell sx={ASA_CELL_BORDER}>
                            {a.label ? (
                              // Not a plain registered account -- Support Email (Sales Hold
                              // Callback traffic, the 1A Support mailbox, and both brands'
                              // Customer Support mailboxes, all pooled -- see
                              // reportingAccountKey's own comment) or a pooled 1A Email / AM
                              // Email row (see getAsaByAccountWithSupportEmailReport's own
                              // comment). Its own pre-built label, not the company/channel
                              // rendering below.
                              a.label
                            ) : (
                              <>
                                {a.company} {CHANNEL_LABELS[a.channel] ?? a.channel}
                                {/* Same disambiguation AHT by account already has: email
                                    registers more than one account under the same
                                    company+channel (e.g. 1A's Website-client real account vs
                                    its Internal-client failure-notice mailbox) -- channel
                                    alone renders identically for both. */}
                                {a.channel === 'm365_email' && a.client && ` · ${a.client}`}
                              </>
                            )}
                          </TableCell>
                          {asaData.weekLabels.map((w) => {
                            const value = a.points[w]
                            const overSla = value != null && a.slaTargetHours != null && value > a.slaTargetHours
                            return (
                              <TableCell key={w} align="right"
                                sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums', ...(overSla && { color: 'error.main', fontWeight: 600 }) }}>
                                {formatAsaHours(value)}
                              </TableCell>
                            )
                          })}
                          <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                            {formatAsaHours(a.slaTargetHours)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Paper>
            ) : null}
          </Box>

          {/* AHT by account per rep -- the account-grouped AHT report above
              (not immediately above -- ASA by account sits between the two,
              see that section's own comment), broken out by agent too. Each
              period's row group can be
              collapsed (client-side, see collapsedAhtRepPeriods) to hide its
              per-agent rows while keeping that period's own Total row
              visible, for shrinking a many-period view down to just the
              summaries. The agent filter is client-side too (poolAgentRows),
              so narrowing it re-pools the Total/grand-Total rows instantly
              with no re-fetch; granularity/periods-back DO re-fetch, since
              they change the actual requested window/bucket size. */}
          <Box id="section-aht-per-rep" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mt: 4, mb: 1 }} title="AHT by account per rep">
              Average handle time and contact counts per agent, broken out by account. "1A Email"/"AM Email"
              pool a brand's Website and Internal-notice accounts together (1A Email = 1A Website + 1A
              Internal; AM Email = AM Website + AM Internal). An
              agent with no contacts in a given period simply has no row that period, not an all-dash one.
            
            </SectionHeading>

            {ahtRepLoading ? (
              <CircularProgress size={24} />
            ) : ahtRepError ? (
              <Alert severity="error">{ahtRepError}</Alert>
            ) : ahtRepData ? (
              <>
                {/* Team chips -- toggles over the Agents selection below
                    (constants/teams.js, a hand copy of Schedule Hub's teams),
                    so several teams can be on at once. No state of their own:
                    a team reads as on while every one of its agents is
                    selected, and clicking it adds or removes exactly its
                    agents. While everyone is selected (the report's starting
                    state) only "All" reads as on, and the first team click
                    narrows to just that team rather than removing it.
                    Clicking "All" selects everyone. Client-side like the
                    Agents filter; the dropdown still fine-tunes. */}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1, rowGap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Team:</Typography>
                  {[null, ...teamsPresent(ahtRepData.agents)].map((team) => {
                    const allSelected = ahtRepData.agents.length > 0 && ahtRepData.agents.every((a) => activeAhtRepAgents.includes(a))
                    const teamAgents = team === null ? ahtRepData.agents : agentsInTeam(ahtRepData.agents, team)
                    const isActive = team === null
                      ? allSelected
                      : !allSelected && teamAgents.length > 0 && teamAgents.every((a) => activeAhtRepAgents.includes(a))
                    const toggle = () => {
                      if (team === null || allSelected) {
                        setSelectedAhtRepAgents(teamAgents)
                        return
                      }
                      const next = isActive
                        ? activeAhtRepAgents.filter((a) => !teamAgents.includes(a))
                        : [...new Set([...activeAhtRepAgents, ...teamAgents])]
                      // Keep the report's own agent order, not click order.
                      setSelectedAhtRepAgents(ahtRepData.agents.filter((a) => next.includes(a)))
                    }
                    return (
                      <Chip
                        key={team ?? 'all'}
                        size="small"
                        label={team ?? 'All'}
                        color={isActive ? 'primary' : 'default'}
                        variant={isActive ? 'filled' : 'outlined'}
                        onClick={toggle}
                      />
                    )
                  })}
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 1, rowGap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Agents</InputLabel>
                    <Select
                      multiple
                      value={activeAhtRepAgents}
                      label="Agents"
                      onChange={(e) => setSelectedAhtRepAgents(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                      renderValue={(selected) => selected.length === ahtRepData.agents.length ? 'All' : `${selected.length} selected`}
                    >
                      {ahtRepData.agents.map((agent) => (
                        <MenuItem key={agent} value={agent}>
                          <Checkbox size="small" checked={activeAhtRepAgents.includes(agent)} />
                          <ListItemText primary={agent} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button size="small" onClick={() => setSelectedAhtRepAgents(ahtRepData.agents)}>Select all</Button>
                  <Button size="small" onClick={() => setSelectedAhtRepAgents([])}>Deselect all</Button>
                  <Button size="small" onClick={() => setCollapsedAhtRepPeriods(new Set(ahtRepData.periods.map((p) => p.label)))}>Collapse all</Button>
                  <Button size="small" onClick={() => setCollapsedAhtRepPeriods(new Set())}>Expand all</Button>
                  <Button size="small" variant="outlined" onClick={exportAhtRepCsv}>Export to Excel</Button>

                  <ToggleButtonGroup size="small" exclusive value={ahtRepGranularity}
                    onChange={(_e, v) => { if (v) setAhtRepGranularityAndResetPeriods(v) }}>
                    {GRANULARITIES.map((g) => <ToggleButton key={g.value} value={g.value}>{g.label}</ToggleButton>)}
                  </ToggleButtonGroup>

                  <ToggleButtonGroup size="small" exclusive value={ahtRepPeriods}
                    onChange={(_e, v) => { if (v) setAhtRepPeriods(v) }}>
                    {AHT_REP_PERIODS_OPTIONS[ahtRepGranularity].map((p) => (
                      <ToggleButton key={p} value={p}>{p} {ahtRepGranularity === 'day' ? 'days' : ahtRepGranularity === 'month' ? 'months' : 'weeks'}</ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Stack>

                <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
                  <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...ASA_CELL_BORDER, minWidth: 160 }}>Period / Agent</TableCell>
                        <TableCell sx={ASA_CELL_BORDER} align="right">Total Contacts</TableCell>
                        <TableCell sx={ASA_CELL_BORDER} align="right">Combined AHT</TableCell>
                        {AHT_REP_ACCOUNTS.map((account) => (
                          <TableCell key={`${account}-contacts`} sx={ASA_CELL_BORDER} align="right">{AHT_REP_ACCOUNT_LABELS[account]} Contacts</TableCell>
                        ))}
                        {AHT_REP_ACCOUNTS.map((account) => (
                          <TableCell key={`${account}-aht`} sx={ASA_CELL_BORDER} align="right">{AHT_REP_ACCOUNT_LABELS[account]} AHT</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ahtRepData.periods.map((period) => {
                        const shownAgents = period.agents.filter((a) => activeAhtRepAgents.includes(a.agent))
                        const periodTotal = poolAgentRows(shownAgents, AHT_REP_ACCOUNTS)
                        const isCollapsed = collapsedAhtRepPeriods.has(period.label)
                        return (
                          <Fragment key={period.label}>
                            <TableRow
                              hover
                              onClick={() => toggleAhtRepPeriod(period.label)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell sx={{ ...ASA_CELL_BORDER, fontWeight: 700 }} colSpan={3 + AHT_REP_ACCOUNTS.length * 2}>
                                <Box component="span" sx={{ display: 'inline-block', width: 14, color: 'text.secondary' }}>
                                  {isCollapsed ? '▸' : '▾'}
                                </Box>
                                {period.label}
                              </TableCell>
                            </TableRow>
                            {!isCollapsed && shownAgents.map((a) => (
                              <TableRow key={`${period.label}-${a.agent}`} hover>
                                <TableCell sx={{ ...ASA_CELL_BORDER, pl: 3 }}>{a.agent}</TableCell>
                                <TableCell sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }} align="right">{fmtCount(a.totalContacts)}</TableCell>
                                <TableCell sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums', ...ahtHighlightSx(a.combinedAht) }} align="right">{formatHandleMs(a.combinedAht)}</TableCell>
                                {AHT_REP_ACCOUNTS.map((account) => (
                                  <TableCell key={`${account}-contacts`} sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }} align="right">
                                    {fmtCount(a.byAccount[account]?.contacts)}
                                  </TableCell>
                                ))}
                                {AHT_REP_ACCOUNTS.map((account) => (
                                  <TableCell key={`${account}-aht`} sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums', ...ahtHighlightSx(a.byAccount[account]?.aht) }} align="right">
                                    {formatHandleMs(a.byAccount[account]?.aht)}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                            <TableRow>
                              <TableCell sx={{ ...ASA_CELL_BORDER, fontWeight: 700 }}>Total</TableCell>
                              <TableCell sx={{ ...ASA_CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }} align="right">{fmtCount(periodTotal.totalContacts)}</TableCell>
                              <TableCell sx={{ ...ASA_CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums', ...ahtHighlightSx(periodTotal.combinedAht) }} align="right">{formatHandleMs(periodTotal.combinedAht)}</TableCell>
                              {AHT_REP_ACCOUNTS.map((account) => (
                                <TableCell key={`${account}-contacts`} sx={{ ...ASA_CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }} align="right">
                                  {fmtCount(periodTotal.byAccount[account]?.contacts)}
                                </TableCell>
                              ))}
                              {AHT_REP_ACCOUNTS.map((account) => (
                                <TableCell key={`${account}-aht`} sx={{ ...ASA_CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums', ...ahtHighlightSx(periodTotal.byAccount[account]?.aht) }} align="right">
                                  {formatHandleMs(periodTotal.byAccount[account]?.aht)}
                                </TableCell>
                              ))}
                            </TableRow>
                          </Fragment>
                        )
                      })}
                      {(() => {
                        const grandTotal = poolAgentRows(
                          ahtRepData.periods.flatMap((period) => period.agents.filter((a) => activeAhtRepAgents.includes(a.agent))),
                          AHT_REP_ACCOUNTS,
                        )
                        return (
                          <TableRow sx={{ borderTop: '2px solid', borderColor: 'divider' }}>
                            <TableCell sx={{ ...ASA_CELL_BORDER, fontWeight: 700 }}>Total</TableCell>
                            <TableCell sx={{ ...ASA_CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }} align="right">{fmtCount(grandTotal.totalContacts)}</TableCell>
                            <TableCell sx={{ ...ASA_CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums', ...ahtHighlightSx(grandTotal.combinedAht) }} align="right">{formatHandleMs(grandTotal.combinedAht)}</TableCell>
                            {AHT_REP_ACCOUNTS.map((account) => (
                              <TableCell key={`${account}-contacts`} sx={{ ...ASA_CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }} align="right">
                                {fmtCount(grandTotal.byAccount[account]?.contacts)}
                              </TableCell>
                            ))}
                            {AHT_REP_ACCOUNTS.map((account) => (
                              <TableCell key={`${account}-aht`} sx={{ ...ASA_CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums', ...ahtHighlightSx(grandTotal.byAccount[account]?.aht) }} align="right">
                                {formatHandleMs(grandTotal.byAccount[account]?.aht)}
                              </TableCell>
                            ))}
                          </TableRow>
                        )
                      })()}
                    </TableBody>
                  </Table>
                </Paper>
              </>
            ) : null}
          </Box>

          <Box id="section-cph" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mt: 4, mb: 1 }} title="Contacts per hour">
              Per-agent efficiency trend — every contact an agent reviewed and acted on (a decision or a manual close,
              each counted on its own even on the same conversation thread), divided by their merged active-claim time,
              attributed to the day the contact was delivered to them. A gap in a line means no covered activity in
              that period, not a zero.
            
            </SectionHeading>

            <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" sx={{ mb: 1, rowGap: 1 }}>
              <ToggleButtonGroup size="small" exclusive value={cphGranularity} onChange={(_e, v) => { if (v) setCphGranularity(v) }}>
                {GRANULARITIES.map((g) => <ToggleButton key={g.value} value={g.value}>{g.label}</ToggleButton>)}
              </ToggleButtonGroup>
            </Stack>

            {cphLoading ? (
              <CircularProgress size={24} />
            ) : cphError ? (
              <Alert severity="error">{cphError}</Alert>
            ) : cphData ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {coverageStartDate
                    ? `Assignment history available from ${coverageStartDate} — a range that starts earlier is allowed, but contacts before that date have no claim on record.`
                    : 'No assignment history recorded yet — check back once agents have claimed and completed some conversations.'}
                  {cphData.totalEvents > 0 && cphData.uncoveredCount > 0 &&
                    ` ${cphData.uncoveredCount} of ${cphData.totalEvents} contacts in this range had no assignment data and are excluded.`}
                </Typography>

                <Paper variant="outlined" sx={{ p: 2, width: TILE_WIDTH, textAlign: 'center', mb: 2 }}>
                  <Typography variant="overline" color="text.secondary">Total active hours</Typography>
                  <Typography variant="h3">{cphTotalActiveHours.toFixed(1)}h</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {activeCphAgents.length === cphAgentsData.length ? 'All agents' : `${activeCphAgents.length} of ${cphAgentsData.length} agents`} — {cphData.from} → {cphData.to}
                  </Typography>
                </Paper>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Agents</InputLabel>
                    <Select
                      multiple
                      value={activeCphAgents}
                      label="Agents"
                      onChange={(e) => setSelectedCphAgents(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                      renderValue={(selected) => selected.length === cphAgentsData.length ? 'All' : `${selected.length} selected`}
                    >
                      {cphAgentsData.map((a) => (
                        <MenuItem key={a.key} value={a.key}>
                          <Checkbox size="small" checked={activeCphAgents.includes(a.key)} />
                          <ListItemText primary={a.key} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button size="small" onClick={() => setSelectedCphAgents(cphAgentsData.map((a) => a.key))}>Select all</Button>
                  <Button size="small" onClick={() => setSelectedCphAgents([])}>Deselect all</Button>
                </Stack>

                <Paper variant="outlined" sx={{ p: 2, width: CHART_WIDTH }}>
                  <LineChart
                    dates={cphAggregated.bucketDates}
                    series={activeCphSeries}
                    valueFmt={(v) => `${v.toFixed(2)}/hr`}
                    height={260}
                  />
                </Paper>
              </>
            ) : null}
          </Box>

          <Box id="section-cphi" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mt: 4, mb: 1 }} title="AHT by intent — trend">
              Average handle time per intent, over time — total time spent handling a given intent across every
              agent (not merged like the per-agent chart above, since two agents working different contacts at once
              are both spending real, separate time), divided by how many contacts of that intent were handled. A
              gap means no covered activity in that period, not a zero.
            
            </SectionHeading>

            <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" sx={{ mb: 1, rowGap: 1 }}>
              <ToggleButtonGroup size="small" exclusive value={cphiGranularity} onChange={(_e, v) => { if (v) setCphiGranularity(v) }}>
                {GRANULARITIES.map((g) => <ToggleButton key={g.value} value={g.value}>{g.label}</ToggleButton>)}
              </ToggleButtonGroup>
            </Stack>

            {cphiLoading ? (
              <CircularProgress size={24} />
            ) : cphiError ? (
              <Alert severity="error">{cphiError}</Alert>
            ) : cphiData ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {cphiCoverageStartDate
                    ? `Assignment history available from ${cphiCoverageStartDate} — a range that starts earlier is allowed, but contacts before that date have no claim on record.`
                    : 'No assignment history recorded yet — check back once agents have claimed and completed some conversations.'}
                  {cphiData.totalEvents > 0 && cphiData.uncoveredCount > 0 &&
                    ` ${cphiData.uncoveredCount} of ${cphiData.totalEvents} contacts in this range had no assignment data and are excluded.`}
                </Typography>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Intents</InputLabel>
                    <Select
                      multiple
                      value={activeCphiIntents}
                      label="Intents"
                      onChange={(e) => setSelectedCphiIntents(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                      renderValue={(selected) => selected.length === cphiIntentsData.length ? 'All' : `${selected.length} selected`}
                    >
                      {cphiIntentsData.map((i) => (
                        <MenuItem key={i.key} value={i.key}>
                          <Checkbox size="small" checked={activeCphiIntents.includes(i.key)} />
                          <ListItemText primary={intentLabel(i.key)} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button size="small" onClick={() => setSelectedCphiIntents(cphiIntentsData.map((i) => i.key))}>Select all</Button>
                  <Button size="small" onClick={() => setSelectedCphiIntents([])}>Deselect all</Button>
                </Stack>

                <Paper variant="outlined" sx={{ p: 2, width: CHART_WIDTH }}>
                  <LineChart
                    dates={cphiAggregated.bucketDates}
                    series={activeCphiSeries}
                    valueFmt={(v) => formatHandleMs(v * 60000)}
                    height={260}
                  />
                </Paper>
              </>
            ) : null}
          </Box>

          <Box id="section-aht-by-intent" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mt: 4, mb: 1 }} title="AHT by intent">
              Which intents take longer to handle, across every agent, for the selected date range. Sorted slowest
              first.
            
            </SectionHeading>
            {htLoading ? (
              <CircularProgress size={24} />
            ) : htError ? (
              <Alert severity="error">{htError}</Alert>
            ) : htData ? (
              <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
                <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...ASA_CELL_BORDER, minWidth: 200 }}>Intent</TableCell>
                      <TableCell sx={ASA_CELL_BORDER} align="right">Contacts</TableCell>
                      <TableCell sx={ASA_CELL_BORDER} align="right">Avg</TableCell>
                      <TableCell sx={ASA_CELL_BORDER} align="right">Median</TableCell>
                      <TableCell sx={ASA_CELL_BORDER} align="right">P90</TableCell>
                      <TableCell sx={ASA_CELL_BORDER} align="right">Max</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {htIntentsRanked.map((i) => (
                      <TableRow key={i.key} hover>
                        <TableCell sx={ASA_CELL_BORDER}>{intentLabel(i.key)}</TableCell>
                        <TableCell sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }} align="right">{fmtCount(i.count)}</TableCell>
                        <TableCell sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }} align="right">{formatHandleMs(i.avgMs)}</TableCell>
                        <TableCell sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }} align="right">{formatHandleMs(i.medianMs)}</TableCell>
                        <TableCell sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }} align="right">{formatHandleMs(i.p90Ms)}</TableCell>
                        <TableCell sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }} align="right">{formatHandleMs(i.maxMs)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            ) : null}
          </Box>

          <Box id="section-aht-by-intent-rep" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mt: 4, mb: 1 }} title="AHT by intent — per rep">
              Every agent's average handle time for one selected intent, against that intent's own team average.
              Rows in red clear both the minimum sample size ({MIN_OUTLIER_COUNT}+ contacts) and the outlier
              threshold ({Math.round(OUTLIER_THRESHOLD_PCT * 100)}%+ slower) — see AHT outliers below for these
              gathered across every intent at once.
            
            </SectionHeading>

            {htLoading ? (
              <CircularProgress size={24} />
            ) : htError ? (
              <Alert severity="error">{htError}</Alert>
            ) : htData ? (
              <>
                <FormControl size="small" sx={{ minWidth: 220, mb: 1 }}>
                  <InputLabel>Intent</InputLabel>
                  <Select value={ahtIntentDrilldownKey ?? ''} label="Intent" onChange={(e) => setSelectedAhtIntentDrilldown(e.target.value)}>
                    {htIntentsRanked.map((i) => (
                      <MenuItem key={i.key} value={i.key}>{intentLabel(i.key)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
                  <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...ASA_CELL_BORDER, minWidth: 200 }}>Agent</TableCell>
                        <TableCell sx={ASA_CELL_BORDER} align="right">Contacts</TableCell>
                        <TableCell sx={ASA_CELL_BORDER} align="right">Avg</TableCell>
                        <TableCell sx={ASA_CELL_BORDER} align="right">Median</TableCell>
                        <TableCell sx={ASA_CELL_BORDER} align="right">P90</TableCell>
                        <TableCell sx={ASA_CELL_BORDER} align="right">Team avg (this intent)</TableCell>
                        <TableCell sx={ASA_CELL_BORDER} align="right">vs. team avg</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ahtIntentDrilldownRows.map((r) => {
                        const isOutlier = r.count >= MIN_OUTLIER_COUNT && r.pctOver != null && r.pctOver >= OUTLIER_THRESHOLD_PCT
                        return (
                          <TableRow key={r.agent} hover>
                            <TableCell sx={ASA_CELL_BORDER}>{r.agent}</TableCell>
                            <TableCell sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }} align="right">{fmtCount(r.count)}</TableCell>
                            <TableCell sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums', ...(isOutlier && { color: 'error.main', fontWeight: 600 }) }} align="right">
                              {formatHandleMs(r.avgMs)}
                            </TableCell>
                            <TableCell sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }} align="right">{formatHandleMs(r.medianMs)}</TableCell>
                            <TableCell sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }} align="right">{formatHandleMs(r.p90Ms)}</TableCell>
                            <TableCell sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }} align="right">
                              {formatHandleMs(r.teamAvgMs)}
                            </TableCell>
                            <TableCell sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums', ...(isOutlier && { color: 'error.main', fontWeight: 600 }) }} align="right">
                              {r.pctOver != null ? `${r.pctOver >= 0 ? '+' : ''}${Math.round(r.pctOver * 100)}%` : '—'}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              </>
            ) : null}
          </Box>

          <Box id="section-aht-outliers" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mt: 4, mb: 1 }} title="AHT outliers">
              Agent × intent pairs at least {Math.round(OUTLIER_THRESHOLD_PCT * 100)}% slower than that intent's
              team average, with at least {MIN_OUTLIER_COUNT} contacts in range — coaching candidates, sorted worst
              first.
              {ahtOutlierRows.length > AHT_OUTLIERS_LIMIT && ` Showing the top ${AHT_OUTLIERS_LIMIT} of ${ahtOutlierRows.length}.`}
            
            </SectionHeading>

            {htLoading ? (
              <CircularProgress size={24} />
            ) : htError ? (
              <Alert severity="error">{htError}</Alert>
            ) : htData ? (
              ahtOutlierRowsShown.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No outliers in this range at the current threshold.</Typography>
              ) : (
                <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
                  <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...ASA_CELL_BORDER, minWidth: 200 }}>Agent</TableCell>
                        <TableCell sx={{ ...ASA_CELL_BORDER, minWidth: 180 }}>Intent</TableCell>
                        <TableCell sx={ASA_CELL_BORDER} align="right">Contacts</TableCell>
                        <TableCell sx={ASA_CELL_BORDER} align="right">Agent avg</TableCell>
                        <TableCell sx={ASA_CELL_BORDER} align="right">Team avg</TableCell>
                        <TableCell sx={ASA_CELL_BORDER} align="right">vs. team avg</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ahtOutlierRowsShown.map((r) => (
                        <TableRow key={`${r.agent}-${r.intent}`} hover>
                          <TableCell sx={ASA_CELL_BORDER}>{r.agent}</TableCell>
                          <TableCell sx={ASA_CELL_BORDER}>{intentLabel(r.intent)}</TableCell>
                          <TableCell sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }} align="right">{fmtCount(r.count)}</TableCell>
                          <TableCell sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums', color: 'error.main', fontWeight: 600 }} align="right">
                            {formatHandleMs(r.avgMs)}
                          </TableCell>
                          <TableCell sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }} align="right">
                            {formatHandleMs(r.teamAvgMs)}
                          </TableCell>
                          <TableCell sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums', color: 'error.main', fontWeight: 600 }} align="right">
                            +{Math.round(r.pctOver * 100)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )
            ) : null}
          </Box>

          <ContactsPerOrder />
          </>
          )}

          {reportingTab === 'legacy' && (
          <>
          <Box id="section-spam-rules" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mt: 4, mb: 1 }} title="By spam rule">
              Daily match trend for the rules selected below, shown either as raw matches or as each rule's share of
              that day's total spam volume — a rate isolates whether a rule itself is catching more or less, rather
              than just riding overall traffic up and down. The table beneath lists every spam rule for the
              selected companies, including rules that matched 0 times and how long it's been since each one last fired.
            
            </SectionHeading>

            <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" sx={{ mb: 1, rowGap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel>Rules</InputLabel>
                <Select
                  multiple
                  value={activeSpamRuleKeys}
                  label="Rules"
                  onChange={(e) => setSelectedSpamRules(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => selected.length === spamRuleTrendData.length ? 'All' : `${selected.length} selected`}
                >
                  {spamRuleTrendData.map((r) => (
                    <MenuItem key={r.key} value={r.key}>
                      <Checkbox size="small" checked={activeSpamRuleKeys.includes(r.key)} />
                      <ListItemText primary={`${r.shortId} — ${r.name} (${r.total})`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button size="small" onClick={() => setSelectedSpamRules(spamRuleTrendData.map((r) => r.key))}>Select all</Button>
              <Button size="small" onClick={() => setSelectedSpamRules([])}>Deselect all</Button>

              <ToggleButtonGroup size="small" exclusive value={spamRuleMetric} onChange={(_e, v) => { if (v) setSpamRuleMetric(v) }}>
                <ToggleButton value="count">Matches</ToggleButton>
                <ToggleButton value="rate">% of spam volume</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, width: CHART_WIDTH }}>
              <LineChart
                dates={data.dates}
                series={activeSpamRuleSeries}
                valueFmt={spamRuleMetric === 'rate' ? (v) => `${Math.round(v * 100)}%` : (v) => Math.round(v).toString()}
                height={260}
              />
            </Paper>

            <Paper variant="outlined" sx={{ mt: 2, width: CHART_WIDTH, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 100 }}>Short ID</TableCell>
                    <TableCell>Rule name</TableCell>
                    <TableCell sx={{ width: 90 }}>Active</TableCell>
                    <TableCell sx={{ width: 90 }} align="right">Matches</TableCell>
                    <TableCell sx={{ width: 120 }}>Last matched</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {spamRulesByCount.map((r) => {
                    const lastMatched = formatLastMatched(r.lastMatchedAt, r.createdAt)
                    return (
                      <TableRow key={r.shortId} hover>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{r.shortId}</TableCell>
                        <TableCell sx={{ color: r.isActive ? 'text.primary' : 'text.secondary' }}>{r.name}</TableCell>
                        <TableCell sx={{ color: r.isActive ? 'text.primary' : 'text.secondary' }}>{r.isActive ? 'Yes' : 'No'}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{r.count}</TableCell>
                        <TableCell sx={{ color: lastMatched.stale ? 'warning.main' : 'text.secondary', fontWeight: lastMatched.stale ? 600 : 400 }}>
                          {lastMatched.label}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Paper>
          </Box>
          </>
          )}

          {reportingTab === 'automation' && (
          <>
          {/* Automation Reports -- rate and failures by flow, weekly. Built
              after digging through git history by hand to explain a
              leadership-slide automation-rate decline: two correctness
              fixes to resend_return_label landed right before the dip, and
              automation_failures has a documented never-retry rule (once a
              conversation fails automation for a flow, it's permanently
              excluded from ever auto-executing that flow again -- see
              automation-failures.js's hasAutomationFailure()). That makes a
              new correctness guard cause a real, LASTING rate decline, not
              a one-week blip. This tab exists so that's visible going
              forward without git-log archaeology. */}
          <SectionHeading variant="subtitle2" appearance="quiet" sx={{ mb: 1 }} title="About this tab">
            Automation rate is automated resolutions ÷ all valid (non-spam) contacts that week, per flow.
            Once a conversation fails automation for a flow it is <strong>never retried</strong> for
            that flow again — a new correctness guard can cause a real, lasting rate decline with no
            change in incoming volume. Check the failures table below for a spike before assuming a
            rate dip on its own is a problem.
          </SectionHeading>

          <ToggleButtonGroup size="small" exclusive value={automationWeeks}
            onChange={(_e, v) => { if (v) setAutomationWeeks(v) }} sx={{ mb: 2 }}>
            {ASA_WEEK_OPTIONS.map((w) => <ToggleButton key={w} value={w}>{w} weeks</ToggleButton>)}
          </ToggleButtonGroup>

          {/* Account-group filter (2026-10-07) -- client-side re-pool, no
              re-fetch (see automationTrendPooling.js). Independent of the
              weeks toggle above, same reasoning every other filter/control
              pairing on this page already follows. */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2, rowGap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Account:</Typography>
            {AUTOMATION_TREND_GROUPS.map((group) => {
              const isActive = selectedAutomationGroups.includes(group)
              const toggle = () => {
                setSelectedAutomationGroups((prev) => isActive ? prev.filter((g) => g !== group) : [...prev, group])
              }
              return (
                <Chip key={group} size="small" label={AUTOMATION_TREND_GROUP_LABELS[group]}
                  color={isActive ? 'primary' : 'default'} variant={isActive ? 'filled' : 'outlined'} onClick={toggle} />
              )
            })}
            <Button size="small" onClick={() => setSelectedAutomationGroups(AUTOMATION_TREND_GROUPS)}>All</Button>
            <Button size="small" onClick={() => setSelectedAutomationGroups([])}>None</Button>
          </Stack>

          <Box id="section-automation-rate" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mb: 1 }} title="Automation rate">
              Share of that week's valid contacts resolved by automation, per flow, pooled across the
              selected account(s) above. Count shown beneath each rate. Only flows with at least one
              automated resolution in this window (for the selected accounts) are listed.
            
            </SectionHeading>

            {automationLoading ? (
              <CircularProgress size={24} />
            ) : automationError ? (
              <Alert severity="error">{automationError}</Alert>
            ) : pooledAutomationRate ? (
              <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
                <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={ASA_CELL_BORDER}>Flow</TableCell>
                      {pooledAutomationRate.weekLabels.map((w) => (
                        <TableCell key={w} sx={{ ...ASA_CELL_BORDER, width: 110 }} align="right">{w}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pooledAutomationRate.flows.map((f) => (
                      <TableRow key={f.flowKey} hover>
                        <TableCell sx={ASA_CELL_BORDER}>{f.displayName}</TableCell>
                        {pooledAutomationRate.weekLabels.map((w) => (
                          <TableCell key={w} align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                            {Math.round(f.points[w] * 100)}%
                            <Typography variant="caption" color="text.secondary" display="block">{f.counts[w]}</Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ ...ASA_CELL_BORDER, fontWeight: 700 }}>Total</TableCell>
                      {pooledAutomationRate.weekLabels.map((w) => {
                        const totalValid = pooledAutomationRate.totalValidPoints[w]
                        const totalAutomated = pooledAutomationRate.totalAutomatedPoints[w]
                        const rate = totalValid > 0 ? totalAutomated / totalValid : 0
                        return (
                          <TableCell key={w} align="right" sx={{ ...ASA_CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                            {Math.round(rate * 100)}%
                            <Typography variant="caption" color="text.secondary" display="block">{totalAutomated}</Typography>
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            ) : null}
          </Box>

          <Box id="section-automation-failures" sx={{ scrollMarginTop: '112px', mt: 4, width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mb: 1 }} title="Automation failures">
              Automation attempts that did NOT complete, per flow, per week — resolved and unresolved
              both count (resolving a failure in Settings → Automation Failures only dismisses it from
              that review queue; it does not restore that conversation's automation eligibility for the
              flow). Only flows with at least one failure in this window are listed.
            
            </SectionHeading>

            {automationLoading ? (
              <CircularProgress size={24} />
            ) : automationError ? (
              <Alert severity="error">{automationError}</Alert>
            ) : pooledAutomationFailures ? (
              <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
                <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={ASA_CELL_BORDER}>Flow</TableCell>
                      {pooledAutomationFailures.weekLabels.map((w) => (
                        <TableCell key={w} sx={{ ...ASA_CELL_BORDER, width: 110 }} align="right">{w}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pooledAutomationFailures.flows.map((f) => (
                      <TableRow key={f.flowKey} hover>
                        <TableCell sx={ASA_CELL_BORDER}>{f.displayName}</TableCell>
                        {pooledAutomationFailures.weekLabels.map((w) => (
                          <TableCell key={w} align="right"
                            sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums', ...(f.counts[w] > 0 && { color: 'warning.main', fontWeight: 600 }) }}>
                            {f.counts[w]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ ...ASA_CELL_BORDER, fontWeight: 700 }}>Total</TableCell>
                      {pooledAutomationFailures.weekLabels.map((w) => (
                        <TableCell key={w} align="right" sx={{ ...ASA_CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                          {pooledAutomationFailures.totalCounts[w]}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            ) : null}

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Review or resolve individual unresolved failures in Settings → Automation Failures.
            </Typography>
          </Box>

          <Box sx={{ mt: 4, maxWidth: 720 }}>
            <SectionHeading variant="subtitle1" sx={{ mb: 1 }} title="Week-over-week summary">
              An AI review of the rate and failure tables above, for the selected account(s) and window
              — what moved, and what's worth looking into. Re-derived from the live data each time, never
              from anything typed here.
            
            </SectionHeading>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Button size="small" variant="outlined" disabled={automationSummaryLoading || selectedAutomationGroups.length === 0}
                onClick={() => generateAutomationSummary(automationSummary != null)}>
                {automationSummaryLoading ? 'Generating…' : automationSummary ? 'Regenerate' : 'Generate summary'}
              </Button>
              {automationSummary?.model && (
                <Typography variant="caption" color="text.secondary">
                  {automationSummary.cached ? 'Cached' : 'Fresh'} · {automationSummary.model} ·{' '}
                  {automationSummary.cost?.usd != null ? `$${automationSummary.cost.usd.toFixed(4)}` : 'cost unknown'}
                </Typography>
              )}
            </Stack>

            {automationSummaryError ? (
              <Alert severity="error">{automationSummaryError}</Alert>
            ) : automationSummaryLoading ? (
              <CircularProgress size={20} />
            ) : automationSummary?.summary ? (
              <Paper variant="outlined" sx={{ p: 2, whiteSpace: 'pre-wrap' }}>
                <Typography variant="body2">{automationSummary.summary}</Typography>
              </Paper>
            ) : automationSummary ? (
              <Typography variant="body2" color="text.secondary">
                Nothing to summarize — no automated resolutions or failures in this window for the selected account(s).
              </Typography>
            ) : null}
          </Box>
          </>
          )}

          {reportingTab === 'dashboard' && (
          <>
          {/* Live supervisor dashboard -- a "right now" snapshot, polled
              every DASHBOARD_POLL_MS while this tab is open (see that
              effect above). Not a date-ranged report like every other tab
              here. Agent state is derived entirely from CXHub's own live
              data (conversation_current_assignment + agent_online_spans) --
              no Schedule Hub break/lunch state in this pass, since Schedule
              Hub has no bulk per-agent endpoint. The live queue is scoped
              to exactly the three real queues agents work today (1A eBay,
              AM eBay, Sales Hold Calls), not a generic channel grid. */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Live snapshot, refreshing automatically every 15s while this tab is open. Not a
            historical report — switch tabs above for date-ranged data.
          </Typography>

          <Box id="section-live-agents" sx={{ scrollMarginTop: '112px', width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mb: 1 }} title="Agent states">
              Every agent currently online or holding a live claim on a contact. "Handled today"
              counts non-spam decisions and manual closes since midnight Eastern, across every channel.
            
            </SectionHeading>

            {dashboardAgentsLoading ? (
              <CircularProgress size={24} />
            ) : dashboardAgentsError ? (
              <Alert severity="error">{dashboardAgentsError}</Alert>
            ) : dashboardAgents && dashboardAgents.length === 0 ? (
              <Typography color="text.secondary">No agents online right now.</Typography>
            ) : dashboardAgents ? (
              <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
                <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={ASA_CELL_BORDER}>Agent</TableCell>
                      <TableCell sx={ASA_CELL_BORDER}>State</TableCell>
                      <TableCell sx={ASA_CELL_BORDER}>Working on</TableCell>
                      <TableCell sx={{ ...ASA_CELL_BORDER, width: 90 }} align="right">Duration</TableCell>
                      <TableCell sx={{ ...ASA_CELL_BORDER, width: 110 }} align="right">Handled today</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardAgents.map((a) => (
                      <TableRow key={a.agent} hover>
                        <TableCell sx={ASA_CELL_BORDER}>{a.displayName}</TableCell>
                        <TableCell sx={ASA_CELL_BORDER}>
                          {a.state === 'working'
                            ? <Chip size="small" label="Working" color="primary" variant="outlined" />
                            : <Chip size="small" label="Idle" variant="outlined" />}
                        </TableCell>
                        <TableCell sx={ASA_CELL_BORDER}>
                          {a.state === 'working'
                            ? `${CHANNEL_LABELS[a.channel] ?? a.channel} · ${a.company ?? ''}`
                            : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                          {a.workingSince ? formatDuration((Date.now() - new Date(a.workingSince).getTime()) / 3600000) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                          {fmtCount(a.handledToday)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            ) : null}
          </Box>

          <Box id="section-live-queue" sx={{ scrollMarginTop: '112px', mt: 4, width: 'fit-content', maxWidth: '100%' }}>
            <SectionHeading variant="subtitle1" sx={{ mb: 1 }} title="Live queue">
              Right now, every queue: 1A and AM eBay, Email and Sales Hold, plus Support Email and
              Tasks. Sales Hold covers both the plain email notice and the callback-request intent,
              combined into one count. 1A/AM Email is the brand's Website mailbox plus its
              internal-notice mailbox, besides Sales Hold. Support Email is the 1A Support and both
              Customer Support mailboxes.
            
            </SectionHeading>

            {dashboardQueuesError ? (
              <Alert severity="error">{dashboardQueuesError}</Alert>
            ) : dashboardQueues ? (
              <Stack direction="row" spacing={3} flexWrap="wrap">
                {dashboardQueues.map((q) => (
                  <Paper key={q.key} variant="outlined" sx={{ p: 2, width: LIVE_QUEUE_TILE_WIDTH, textAlign: 'center' }}>
                    <Typography variant="overline" color="text.secondary">{q.label}</Typography>
                    <Typography variant="h3">{fmtCount(q.waiting)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {q.oldestCreatedAt
                        ? `Oldest: ${formatDuration((Date.now() - new Date(q.oldestCreatedAt).getTime()) / 3600000)}`
                        : 'Nothing waiting'}
                    </Typography>
                    {/* Parked contacts (Lab-only, 2026-10-02) are set aside by
                        their owner, so they are their own count, never part
                        of the waiting number above. Shown only when any. */}
                    {q.parked > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block" data-testid="live-queue-parked">
                        {fmtCount(q.parked)} parked
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Stack>
            ) : (
              <CircularProgress size={24} />
            )}
          </Box>

          {/* A more granular breakdown of the same live backlog, added below
              the boxes above (not replacing them) -- 1A/AM/Support sections,
              each with a real Wait Time column, mirroring the shape of the
              team's own reference live dashboard. See getLiveQueueDetail()'s
              own comment (repositories/reporting.js) for the full row-by-row
              mapping and why "1A/AM Email" is a catch-all rather than
              eBay-specific -- isolating eBay return notices from everything
              else in that mailbox needs a signal that's only in per-message
              metadata today, not a queryable column; deferred as its own
              follow-up rather than bundled here, specifically to ship this
              with zero schema change and zero agent-facing risk. */}
          <Box sx={{ mt: 4 }}>
            <SectionHeading variant="subtitle1" sx={{ mb: 1 }} title="Live queue detail">
              A more granular breakdown of the same live backlog, awaiting action -- excludes
              anything an agent already has claimed. "1A/AM Email" is the brand's Website mailbox
              plus its internal-notice mailbox, besides Sales Hold. 1A Sales Hold Callback has no AM
              equivalent -- AM doesn't run that flow.
            
            </SectionHeading>

            {dashboardQueuesError ? (
              <Alert severity="error">{dashboardQueuesError}</Alert>
            ) : dashboardQueueDetail ? (
              <Stack direction="row" spacing={3} flexWrap="wrap">
                {['1A', 'AM', 'Support'].map((section) => {
                  const rows = dashboardQueueDetail.filter((r) => r.section === section)
                  if (rows.length === 0) return null
                  return (
                    <Paper key={section} variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
                      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={ASA_CELL_BORDER} colSpan={3}>
                              <Typography variant="subtitle2">{section}</Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={ASA_CELL_BORDER}>Queue</TableCell>
                            <TableCell sx={{ ...ASA_CELL_BORDER, width: 90 }} align="right">In Queue</TableCell>
                            <TableCell sx={{ ...ASA_CELL_BORDER, width: 110 }} align="right">Wait Time</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map((r) => (
                            <TableRow key={r.key} hover>
                              <TableCell sx={ASA_CELL_BORDER}>{r.label}</TableCell>
                              <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                                {fmtCount(r.waiting)}
                              </TableCell>
                              <TableCell align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                                {r.oldestCreatedAt ? formatDuration((Date.now() - new Date(r.oldestCreatedAt).getTime()) / 3600000) : '—'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  )
                })}
              </Stack>
            ) : (
              <CircularProgress size={24} />
            )}
          </Box>
          </>
          )}

          {reportingTab === 'weekly' && (
          <>
          {/* Weekly cxHub export for the Customer Service reporting
              pipeline -- both CSVs are built directly from the SAME report
              functions the tabs above call (services/weekly-export.js),
              never transcribed off the dashboard, so the numbers always
              match. Each button computes its own fixed window server-side
              (trailing 6 weeks / most recently completed week) -- the
              shared Company/From-To controls above don't apply here, same
              as the Dashboard tab. */}
          <Box sx={{ maxWidth: 640 }}>
            <SectionHeading variant="subtitle1" sx={{ mb: 1 }} title="Weekly export">
              10 rows per week — 1A eBay, AM eBay, eBay Total, 1A Email,
              AM Email, Email Total, Support Email, 1A Total, AM Total, and
              Grand Total — for the trailing 6 weeks, so a fresh export
              always overlaps the prior one. Each row carries its channel
              (eBay / Email / Support / All), contact/message balance
              (including the automated/auto-closed/legacy breakdown behind
              "resolved by system"), AHT (average and median), and ASA/SLA
              figures, pulled directly from cxHub's own reports. Total rows
              have no AHT median or SLA target of their own.
            
            </SectionHeading>
            <Button variant="contained" onClick={handleDownloadWeeklyExport} disabled={weeklyExportBusy}
              startIcon={weeklyExportBusy ? <CircularProgress size={14} color="inherit" /> : null}>
              {weeklyExportBusy ? 'Preparing…' : 'Download weekly export'}
            </Button>
            {weeklyExportError && <Alert severity="error" sx={{ mt: 1 }}>{weeklyExportError}</Alert>}

            {/* Combined labor utilization (Schedule Hub, added 2026-09-18,
                combined into one file 2026-09-24) -- one company-wide row
                ("Company Total") plus one row per Schedule Hub team
                (Management excluded from the team rows only, Patrick's
                call, 2026-09-22), for the most recently completed
                Sunday-Saturday week (matching cxHub's own convention, not
                Schedule Hub's native Monday-Sunday). Occupancy/Utilization
                are computed from Schedule Hub's own handling_hours field
                plus, on the company row only, CXHub's own handling time --
                see buildLaborUtilizationCombinedRows()'s own comment for
                exactly how, and why team rows can't get the same CXHub
                addition (no agent-to-team mapping exists in CXHub). */}
            <SectionHeading variant="subtitle1" sx={{ mt: 4, mb: 1 }} title="Labor utilization">
              Scheduled, VTO, PTO, absence, FMLA, overtime, available hours, and
              Occupancy/Utilization from Schedule Hub, for the most recently completed
              Sunday–Saturday week -- one company-wide row ("Company Total") plus one
              row per Schedule Hub team (Management excluded from team rows only).
            
            </SectionHeading>
            <Button variant="outlined" onClick={handleDownloadLabor} disabled={laborBusy}
              startIcon={laborBusy ? <CircularProgress size={14} color="inherit" /> : null}>
              {laborBusy ? 'Preparing…' : 'Download labor utilization'}
            </Button>
            {laborError && <Alert severity="error" sx={{ mt: 1 }}>{laborError}</Alert>}
          </Box>
          </>
          )}

          {reportingTab === 'qa-kpi' && (
          <>
          {/* Visibility/audit view (2026-09-30) -- a preview of the aggregate
              metrics CXHub plans to expose to QA System's own KPI Admin via a
              separate, not-yet-built read-only API (see docs/decisions.md and
              the QA System reporting API project memory). NOT the
              integration itself: QA System will pull its own data
              server-to-server once that API exists; this tab just shows an
              admin what the numbers look like today. Its own fetch
              (qaKpiAsaData, 2026-10-15), not a reuse of asaData below -- see
              QA_KPI_ASA_ACCOUNTS's own comment for why. */}
          <Box id="section-qa-kpi-asa" sx={{ scrollMarginTop: '112px', maxWidth: 640, width: 'fit-content' }}>
            <SectionHeading variant="subtitle1" sx={{ mb: 1 }} title="QA KPI Dashboard">
              Preview of what will be exposed to QA System's KPI Admin — starting with ASA (average speed of
              answer) by account. The same numbers are served to QA System through the QA API
              (GET /api/qa/kpi/asa-by-account). Every account shows even with no data yet.
            
            </SectionHeading>
          </Box>

          <Box sx={{ maxWidth: 640 }}>
            <SectionHeading variant="subtitle1" sx={{ mt: 2, mb: 1 }} title="ASA by account" />

            {qaKpiAsaLoading ? (
              <CircularProgress size={24} />
            ) : qaKpiAsaError ? (
              <Alert severity="error">{qaKpiAsaError}</Alert>
            ) : qaKpiAsaData ? (
              <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
                <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={ASA_CELL_BORDER}>Account</TableCell>
                      {qaKpiAsaData.weekLabels.map((w) => (
                        <TableCell key={w} sx={{ ...ASA_CELL_BORDER, width: 110 }} align="right">{w}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {QA_KPI_ASA_ACCOUNTS.map((key) => {
                      // Always shown, even with no data anywhere in the window --
                      // unlike the Account Reports ASA table below (which hides a
                      // dataless row), this is a fixed, named preview of exactly
                      // the accounts QA System will see, so a missing row here
                      // must always mean "not built yet," never "no traffic this
                      // week."
                      const a = qaKpiAsaData.accounts.find((acc) => acc.account === key)
                      return (
                        <TableRow key={key} hover>
                          <TableCell sx={ASA_CELL_BORDER}>{QA_KPI_ASA_ACCOUNT_LABELS[key]}</TableCell>
                          {qaKpiAsaData.weekLabels.map((w) => (
                            <TableCell key={w} align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                              {formatAsaHours(a?.points?.[w])}
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </Paper>
            ) : null}
          </Box>

          {/* AHT's -- ONE blended, company-wide weekly number, labeled "Email
              AHT" (Patrick's call, 2026-10-01): both eBay accounts plus both
              brands' Website and Internal-notice mailboxes pooled into a
              single true average, excluding the Support Email carve-out
              (1A_INTERNAL_EMAIL's Sales Hold Callback traffic) and
              TEST_EMAIL. Deliberately NOT split by brand or channel, unlike
              every other AHT table on this page -- see
              getBlendedEmailAhtReport's own module comment for exactly
              what's pooled. */}
          <Box id="section-qa-kpi-aht" sx={{ scrollMarginTop: '112px', maxWidth: 640, width: 'fit-content' }}>
            <SectionHeading variant="subtitle1" sx={{ mt: 4, mb: 1 }} title="AHT's">
              One blended average handle time across every live customer-facing account — 1A/AM eBay, both
              brands' Website mailboxes, and both brands' internal failure-notice mailboxes — pooled into a
              single number, not split by brand or channel. Excludes Support Email (Sales Hold Callback
              traffic) and TEST_EMAIL.
            
            </SectionHeading>

            {qaKpiAhtLoading ? (
              <CircularProgress size={24} />
            ) : qaKpiAhtError ? (
              <Alert severity="error">{qaKpiAhtError}</Alert>
            ) : qaKpiAhtData ? (
              <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
                <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={ASA_CELL_BORDER}></TableCell>
                      {qaKpiAhtData.weekLabels.map((w) => (
                        <TableCell key={w} sx={{ ...ASA_CELL_BORDER, width: 110 }} align="right">{w}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow hover>
                      <TableCell sx={ASA_CELL_BORDER}>Email AHT</TableCell>
                      {qaKpiAhtData.weekLabels.map((w) => (
                        <TableCell key={w} align="right" sx={{ ...ASA_CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                          {formatHandleMs(qaKpiAhtData.points?.[w])}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            ) : null}
          </Box>
          </>
          )}

          {reportingTab === 'scorecards' && (
          <>
          <Box sx={{ mb: 2 }}>
            <SectionHeading variant="subtitle2" appearance="quiet" title="About this tab">
              Every agent's CXHub stats, in one supervisor-facing view — a Total Emails count and Email AHT
              (everything they resolved in CXHub: eBay plus both brands' Website and Internal-notice mailboxes,
              Support Email excluded), Support Email AHT (1A's Sales Hold Callback traffic, the 1A Support
              mailbox, and both brands' Customer Support mailboxes, shown only once someone on the selected team
              has handled any), and schedule adherence from Schedule Hub. Where Bright Pattern email numbers have
              been entered from Power BI (below), BP Emails, BP Email AHT and Combined Email AHT (BP plus CXHub
              email and Support Email, weighted by count) are shown too, for the periods entered. Select one or
              more weeks/months to pool together into the table below.
            </SectionHeading>
            {/* The notice stays visible: it is a caution, not a description. */}
            <Typography variant="caption" color="text.secondary" component="p">{HT_NOT_A_PERFORMANCE_MEASURE}</Typography>
          </Box>

          <BpEmailStatsPanel />

          {/* ONE Team filter for both tables below (2026-10-08) -- picking a
              team here overwrites both Week's and Month's own Agents
              selection at once (selectScorecardsTeam), each resolved against
              that table's own agent roster. The union of both tables' agents
              decides which team chips show, so a team only visible in one
              granularity's window still appears. Multiple teams at once
              (2026-10-13): "All" is active when scSelectedTeams is null,
              an individual team's chip is active when it's in the array --
              same toggle convention as AHT by account per rep's Team chips. */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2, rowGap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Team:</Typography>
            {[null, ...teamsPresent([...new Set([...(scWeekData?.agents ?? []), ...(scMonthData?.agents ?? [])])])].map((team) => {
              const isActive = team === null ? scSelectedTeams === null : (scSelectedTeams?.includes(team) ?? false)
              return (
                <Chip key={team ?? 'all'} size="small" label={team ?? 'All'}
                  color={isActive ? 'primary' : 'default'}
                  variant={isActive ? 'filled' : 'outlined'}
                  onClick={() => selectScorecardsTeam(team)} />
              )
            })}
          </Stack>

          {/* The weekly analysis sits beside the Week table (2026-10-19,
              Patrick's sketch) and wraps below it on a narrow screen. It
              follows the Team chips above. */}
          <Stack direction="row" spacing={3} alignItems="flex-start" flexWrap="wrap" useFlexGap>
            <ScorecardTable
              id="section-scorecards-week" title="Week" periodLabel="Week ending"
              loading={scWeekLoading} error={scWeekError} data={scWeekData} pooled={scWeek}
              selectedPeriods={selectedScWeekPeriods} setSelectedPeriods={setSelectedScWeekPeriods}
              selectedAgents={selectedScWeekAgents} setSelectedAgents={setSelectedScWeekAgents}
              exportFilename="scorecards-week.csv"
            />
            <ScorecardsFocusPanel teams={scSelectedTeams} notice={HT_NOT_A_PERFORMANCE_MEASURE} />
          </Stack>

          <ScorecardTable
            id="section-scorecards-month" title="Month" periodLabel="Month"
            loading={scMonthLoading} error={scMonthError} data={scMonthData} pooled={scMonth}
            selectedPeriods={selectedScMonthPeriods} setSelectedPeriods={setSelectedScMonthPeriods}
            selectedAgents={selectedScMonthAgents} setSelectedAgents={setSelectedScMonthAgents}
            exportFilename="scorecards-month.csv"
          />
          </>
          )}

          {/* Forecasting (Patrick's own working area, see canSeeForecastingTab). Phone &
              Chat and Email are built, switched in ForecastingTab; Support and an
              Overview are not built yet. */}
          {reportingTab === 'forecasting' && showForecastingTab && (
          <Box sx={{ mb: 2 }}>
            <ForecastingTab />
          </Box>
          )}
        </>
      ) : null}
    </Box>
  )
}
